import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Box } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import type { GridRowSelectionModel } from '@mui/x-data-grid';
import { plPL } from '@mui/x-data-grid/locales';
import { buildingsApi, inspectionsApi, ceebAcceptApi, ceebFlagsApi, clientsApi, termsApi } from '../services/api';
import { useProtocolPdfsPack } from '../hooks/useProtocolPdfsPack';
import { ProtocolYearSelect, currentProtocolYear } from './ProtocolYearSelect';
import { useSummaryHeatSources } from '../hooks/useSummaryHeatSources';
import { SummaryHeatSourcesOption } from './SummaryHeatSourcesOption';
import type {
  PipelineSortDir,
  LifecycleState, PipelineBuilding, PipelineSort, InspectionConflictError, Client,
  SummariesZipFilters, CeebNightlyImportStatus, CeebPushBatchStatus, CeebAcceptBatchStatus, CeebPushStatusResponse,
  CeebPushRunningResponse, CeebPushProgress, CeebPushRetryInfo, CeebPushReport,
} from '../services/api';
import BuildingInspectionView from './BuildingInspectionView';
import AddBuildingModal from './AddBuildingModal';
import { STATE_ORDER, LIFECYCLE_META, stageCount, stageOf, stageBadgeLabel } from './lifecycleMeta';
import { Button } from '../ds/components/Button';
import { Icon } from '../ds/components/Icon';
import { useAuthOptional } from '../contexts/AuthContext';
import { ProgressBar } from '../ds/components/ProgressBar';
import { ModalShell } from '../ds/ModalShell';
import { InputField } from '../ds/InputField';
import { ComboBox } from '../ds/ComboBox';
import { clientComboOptions, clientLabel } from '../utils/clientOptions';
import CeebBotReport from './CeebBotReport';
import { useLastCeebMeta } from '../hooks/useLastCeebMeta';
import { useCeebBatchPolling, CeebBatchApi } from '../hooks/useCeebBatchPolling';
import { useCeebAcceptPolling, mailLabel, CeebAcceptOutcome } from '../hooks/useCeebAcceptPolling';
import CeebAcceptModal, { CeebAcceptTarget } from './CeebAcceptModal';
import { ConfirmModal } from '../ds/ConfirmModal';
import { termDateError, termDateBounds } from '../utils/termDate';
import { romanTerm } from '../utils/attempts';
import { budynkiLabel, doneOfApartmentsLabel, lokaleLabel } from '../utils/plural';
import { asciiFilename, saveBlob } from '../utils/download';
import { FlueWarningIcon } from './FlueWarningIcon';
import { ceebProtocolLabel, ceebProtocolShortNote } from '../utils/ceebProtocolLabel';
import { awaitsAcceptance } from '../utils/ceebAcceptance';
import { ceebDeletedFull, ceebDeletedShort } from '../utils/ceebDeletedProtocol';
import HeatSourceSummaryModal from './HeatSourceSummaryModal';
import UncheckedApartmentsModal from './UncheckedApartmentsModal';
import ScheduleMailModal from './ScheduleMailModal';
import CaseNumbersModal from './CaseNumbersModal';
import PipelineBulkHelpModal from './PipelineBulkHelp';
import CeebEditIssuedModal from './CeebEditIssuedModal';
import { InaccessiblePushConfirm } from './InaccessiblePushConfirm';
import { canPushInaccessible } from '../utils/ceebInaccessiblePush';
// A11/A12 (uwagi Bronka, raport 2026-10-19): import harmonogramu z Excela + zarządca/lokale przy planowaniu.
import ScheduleImportModal from './ScheduleImportModal';
import PlanClientFields, {
  EMPTY_PLAN_CLIENT, planClientError, planClientSummary, savePlanClient,
} from './PlanClientFields';
import type { PlanClientOutcome, PlanClientValue } from './PlanClientFields';
import PlanPrefillSourceField, { PREFILL_SOURCE_DEFAULT } from './PlanPrefillSourceField';
import { apartmentCountError } from '../utils/apartmentCount';
import { QueueModal, QueuedBadge, canQueue, isQueued } from './InspectionQueue';
import { CeebEditIssuedStatus } from './CeebEditIssuedStatus';
import {
  canEditIssued, acceptedNeedsManualFix, ACCEPTED_MANUAL_FIX_HINT, offerEditIssuedAfterPush, editIssuedFields,
} from '../utils/ceebEditIssued';

// PipelineView — skalowalny widok budynków wg workflow kominiarza (spec §3 + §1a).
// DWA NIEZALEŻNE WYMIARY budynku:
//   1) postęp terenowy (lifecycle_state + apt_terminal/apt_total/apt_open) — STERUJE etapem;
//   2) status CEEB (apt_in_ceeb / apt_pending_ceeb) — OSOBNY wymiar wysyłki per-lokal.
// Dlatego akcji może być KILKA naraz: budynek DO_POWROTU z terminalnymi lokalami poza CEEB
// pokazuje jednocześnie [Wyślij do CEEB] (apt_pending_ceeb>0) i [Zaplanuj II termin] (apt_open>0,
// tylko gdy II termin jest wolny — III+ termin bez przycisku, właściciel 2026-10-02).

// Meta stanów (STATE_ORDER, LIFECYCLE_META) współdzielone z DashboardView — lifecycleMeta.ts.
// Grupa „Aktywne" = pipeline w toku (spec §2) — domyślny widok (NIE 3771 NOWYCH).
// W_CEEB w aktywnych (właściciel 2026-07-27): od migr. 037 = protokół WYSTAWIONY w CEEB
// („Wprowadzony"), więc czeka na zielony przycisk superadmina — musi być widoczny.
// B7: dymek kolumny „Gotowość terenowa" — co znaczą obie liczby (apt_terminal / apt_total z resolvera).
export const READINESS_HINT = 'Zrobione = lokale z wynikiem zamykającym w bieżącym przeglądzie (brak uwag, zalecenia, '
  + 'odmowa wpisu do rejestru CEEB). Druga liczba = wszystkie lokale budynku. Nieobecność i odmowa lokatora '
  + 'nie zamykają lokalu — trzeba do niego wrócić.';

const ACTIVE_STATES: LifecycleState[] = ['ZAPLANOWANY', 'W_TERENIE', 'DO_POWROTU', 'GOTOWY_DO_CEEB', 'W_CEEB'];

// Partie CEEB — API stabilne (stałe modułu), bo są zależnością hooka `useCeebBatchPolling`.
const PUSH_BATCH_API: CeebBatchApi<CeebPushBatchStatus> = {
  start: (ids) => buildingsApi.pushToCeebBatch(ids),
  status: (jobId) => buildingsApi.ceebPushBatchStatus(jobId),
};
const ACCEPT_BATCH_API: CeebBatchApi<CeebAcceptBatchStatus> = {
  start: (ids) => ceebAcceptApi.startBatch(ids),
  status: (jobId) => ceebAcceptApi.batchStatus(jobId),
};

/** Protokół już wystawiony w CEEB — bot zmienia tylko wersje robocze, więc „Wyślij do CEEB" nic tu nie da. */
const protocolIssued = (b: PipelineBuilding) => b.ceebStatus === 'Wprowadzony' || b.ceebStatus === 'Zaakceptowany';

// Stany, w których można ZAPLANOWAĆ nowy przegląd bez konfliktu 409 (właściciel 2026-07-09).
// NOWY = brak przeglądu; ZAMKNIETY = poprzedni przegląd COMPLETED → nowy roczny cykl.
// Wszystkie pozostałe stany mają AKTYWNĄ (nie-COMPLETED) inspekcję, więc POST /inspections
// dałby 409 (guard #46: `status <> 'COMPLETED'`; resolver mig. 025 mapuje ZAMKNIETY⟺COMPLETED).
// Dla nich [Zaplanuj] (nowy) jest mylący — nawigację do istniejącego pokrywa [Otwórz].
const PLANNABLE_STATES: LifecycleState[] = ['NOWY', 'ZAMKNIETY'];

// pkt 14 (właściciel 2026-09-13): „Zakres kontroli" w CEEB (liczby przewodów) wpisują kominiarze
// ręcznie w CEEB — bot ich nie dotyka. Puste/zero po nocnym imporcie = ostrzeżenie (wysłać można).

const SORT_OPTIONS: { value: PipelineSort; label: string }[] = [
  // pkt 13 (właściciel 2026-09-14): domyślnie „jak w CEEB” — po ostatniej aktywności na budynku.
  { value: 'ceeb_activity', label: 'Ostatnia aktywność w CEEB' },
  { value: 'readiness', label: 'Gotowość terenowa' },
  { value: 'ceeb_pending', label: 'CEEB: czeka najwięcej' },
  { value: 'term', label: 'Najbliższy termin' },
  { value: 'first_term', label: 'Data I terminu' },
  { value: 'second_term', label: 'Data II terminu' },
  { value: 'ceeb_term', label: 'Termin następnej kontroli (CEEB)' },
  { value: 'address', label: 'Adres' },
];

// Sortowanie kliknięciem w nagłówek (właściciel 2026-09-13, pkt 25): pole kolumny → sort API.
// Kierunek jest zaszyty w backendzie (ORDER_BY), więc klik ustawia tylko kryterium.
// Bronek 14.09 („sortować wg kolumny jak w Excelu”): „Termin I” i „Termin II” — każda po swojej dacie.
const FIELD_TO_SORT: Record<string, PipelineSort> = {
  address: 'address',
  next_inspection_date: 'ceeb_term',
  ceeb_last_activity: 'ceeb_activity',
  readiness: 'readiness',
  ceeb: 'ceeb_pending',
  first_term: 'first_term',
  second_term: 'second_term',
};
// Domyślny kierunek per sort — lustro backendu (routes/pipelineQuery.js DEFAULT_DIR).
const SORT_DEFAULT_DIR: Record<PipelineSort, PipelineSortDir> = {
  ceeb_activity: 'desc', readiness: 'desc', term: 'asc', address: 'asc', ceeb_pending: 'desc', ceeb_term: 'asc',
  first_term: 'asc', second_term: 'asc',
};
// `term` (najbliższy termin) nie ma własnej kolumny — wtedy żaden nagłówek nie pokazuje strzałki.
const SORT_TO_FIELD: Record<PipelineSort, string | null> = {
  address: 'address', ceeb_term: 'next_inspection_date', ceeb_activity: 'ceeb_last_activity', readiness: 'readiness', ceeb_pending: 'ceeb',
  term: null, first_term: 'first_term', second_term: 'second_term',
};

// #52 (B8) — etykiety statusu istniejącej inspekcji w komunikacie 409 (spójne z InspectionsTab).
const INSPECTION_STATUS_LABELS: Record<string, string> = {
  PLANNED: 'Zaplanowane',
  IN_PROGRESS: 'W realizacji',
  READY_FOR_CEEB: 'Do CEEB',
  COMPLETED: 'Zrealizowane',
};

// DataGrid sx — restyle MUI chrome z DS-tokenami (spójne z InspectionStatusView).
const dataGridSx = {
  border: 'var(--border-width) solid var(--border)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--surface-card)',
  fontFamily: 'var(--font-sans)',
  color: 'var(--text-primary)',
  '& .MuiDataGrid-columnHeaders': {
    background: 'var(--surface-fill)',
    borderBottom: 'var(--border-width) solid var(--border)',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontFamily: 'var(--font-sans)',
    fontSize: 'var(--text-label)',
    fontWeight: 'var(--weight-semibold)',
    color: 'var(--text-secondary)',
  },
  '& .MuiDataGrid-cell': {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    py: 1,
    fontFamily: 'var(--font-sans)',
    fontSize: 'var(--text-label)',
    color: 'var(--text-primary)',
    borderBottom: 'var(--border-width) solid var(--border)',
  },
  '& .MuiDataGrid-row': { minHeight: '64px !important' },
  '& .MuiDataGrid-row:hover': { background: 'var(--surface-hover)' },
  '& .MuiDataGrid-footerContainer': { borderTop: 'var(--border-width) solid var(--border)' },
} as const;

const actionBtnStyle: React.CSSProperties = {
  minHeight: 38,
  padding: '0 var(--space-3)',
  fontSize: 'var(--text-caption)',
};

// 'YYYY-MM-DD' → 'DD.MM.YYYY' (bez godzin, spójnie z konwencją pl-PL). Null → '—'.
function formatTermDate(d: string | null): string {
  if (!d) return '—';
  const p = d.split('-');
  return p.length === 3 ? `${p[2]}.${p[1]}.${p[0]}` : d;
}

type PushState = {
  status: 'running' | 'done' | 'error';
  synced?: number;
  error?: string;
  // Zadanie 3.3/3.4 (2026-08-19): licznik SUKCESÓW z kontekstem + treść awarii w kolumnie statusu.
  apartmentCount?: number;
  saved?: boolean;
  markReason?: string;
  perApartment?: Array<{ nr: string; synced: boolean; reason: string; code?: string }>;
  errors?: Array<{ step: string; error: string }>;
  /** Sprzeczne dane budynku (2026-08-31) — ostrzeżenie, nie blokada. */
  ostrzezeniaDanych?: Array<{ pole: string; problem: string; wartosc: string }>;
  /** Czy rejestr POTWIERDZIŁ finalizację (2026-09-01) — sam klik bota nie wystarcza. */
  statusConfirm?: { stamp: boolean; status: string | null; reason: string | null } | null;
  /** Komunikat backendu (2026-10-10): drugi protokół w tym roku zapisany tylko jako wersja robocza. */
  notice?: string | null;
  /** E2 Z4: którą akcję ponowić po świadomym obejściu odmowy TABLET_PENDING (superadmin). 'inaccessible' = bez obejścia. */
  retry?: 'push' | 'finalize' | 'inaccessible';
  /** D1 (2026-10-22): postęp trwającej wysyłki z serwera („Lokal 5 z 13 · 6 min"). */
  progress?: CeebPushProgress | null;
  /** D3: wynik po automatycznym ponowieniu (serwer ponowił raz po stanie przejściowym CEEB). */
  retried?: CeebPushRetryInfo | null;
} & Partial<Pick<CeebPushStatusResponse,
  // Poprawka wystawionego protokołu (2026-10-09): tryb, próba bez zapisu, krok, kod/next/status odmowy, alarm,
  // kopie protokołu przed/po, raport.
  'mode' | 'dryRun' | 'step' | 'code' | 'next' | 'ceebStatus' | 'alarm' | 'editIssued' | 'reportUrl' | 'copyBlocked'
  | 'snapshotBeforeId' | 'snapshotAfterId' | 'defaultConnected'>>;

// Chip filtra stanu z licznikiem (klik = filtr; spec §3).
function ChipButton({
  testId, label, count, active, tintFg, onClick,
}: {
  testId: string; label: string; count: number; active: boolean; tintFg?: string; onClick: () => void;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={active}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: '6px var(--space-3)',
        borderRadius: 'var(--radius-pill)',
        border: `var(--border-width-strong) solid ${active ? 'var(--action-primary)' : 'var(--border-strong)'}`,
        background: active ? 'var(--changed-bg)' : 'var(--surface-card)',
        color: 'var(--text-primary)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-label)',
        fontWeight: active ? 'var(--weight-bold)' : 'var(--weight-medium)',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      <span>{label}</span>
      <span
        data-testid={`${testId}-count`}
        style={{
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 'var(--weight-bold)',
          color: tintFg || 'var(--text-secondary)',
        }}
      >
        {count}
      </span>
    </button>
  );
}

// #404 (D6): budynek z wstrzymanym drugim protokołem w roku — ślad reguły albo zwolnienie przy ZAMKNIETY.
function drugiProtokolDoFinalizacji(b: PipelineBuilding | null): boolean {
  return !!b && (b.secondProtocolReleasable === true || Array.isArray(b.secondProtocolIssued));
}
function numeryDrugiegoProtokolu(b: PipelineBuilding | null): string {
  const lista = (b && b.secondProtocolIssued) || [];
  return lista.length
    ? lista.map((p) => `${p.caseNumber}${p.status ? ` „${p.status}”` : ''}`).join(', ')
    : 'numery do sprawdzenia w CEEB';
}

// Pigułka etapu (lifecycle_state) — wymiar terenowy.
// DO_POWROTU (właściciel 2026-10-02): ten sam etap „Gotowy", ale pomarańczowa odznaka
// „Gotowy · N nieobecnych" — widać, że budynek jest na liście gotowych, a mimo to trzeba wrócić.
function LifecycleChip({ state, aptOpen }: { state: LifecycleState; aptOpen?: number }) {
  const m = LIFECYCLE_META[state] || LIFECYCLE_META.NOWY;
  return (
    <span
      data-testid={`lifecycle-chip-${state}`}
      title={state === 'DO_POWROTU' ? 'Sprawdzone lokale można wysłać do CEEB; do lokali z nieobecnością lub odmową lokatora trzeba wrócić (II termin). Liczba = lokale do powrotu.' : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 10px',
        borderRadius: 'var(--radius-pill)',
        background: m.bg,
        color: m.fg,
        fontSize: 'var(--text-label)',
        fontWeight: 'var(--weight-semibold)',
        whiteSpace: 'nowrap',
      }}
    >
      {stageBadgeLabel(state, aptOpen)}
    </span>
  );
}

// Kolumna „Status CEEB" — drugi wymiar (§1a): „X w CEEB / Y czeka" + stan wysyłki.
function CeebStatusCell({ row, push, onEditIssued, onTabletOverride }: {
  row: PipelineBuilding; push?: PushState; onEditIssued?: () => void; onTabletOverride?: () => void;
}) {
  // Poprawka wystawionego protokołu (2026-10-09) ma własne komunikaty (próba bez zapisu, alarm) — CeebEditIssuedStatus.
  const editMode = push?.mode === 'edit-issued';
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', py: 'var(--space-2)' }}>
      <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>
        {row.apt_in_ceeb} w CEEB
        {row.apt_pending_ceeb > 0 && (
          <>
            {' '}
            <span style={{ color: 'var(--status-rec)', fontWeight: 'var(--weight-semibold)' }}>
              / {row.apt_pending_ceeb} czeka
            </span>
          </>
        )}
      </span>
      {editMode && push && <CeebEditIssuedStatus buildingId={row.id} push={push} />}
      {/* #340: alarm kopii lokali w zwykłej wysyłce — wszystkie zapisy bota w CEEB wstrzymane do decyzji człowieka. */}
      {!editMode && push && push.status !== 'running' && push.alarm && (
        <span role="alert" style={{ fontSize: 'var(--text-caption)', color: 'var(--status-deny)', fontWeight: 'var(--weight-semibold)' }}>
          ALARM: wszystkie zapisy bota w CEEB wstrzymane — {push.alarm.message}
        </span>
      )}
      {push?.status === 'running' && !editMode && (
        // D1 (2026-10-22): etap z serwera zamiast samego „Wysyłanie…" (Budowlana 108: 13 min bez znaku życia).
        <span data-testid={`ceeb-push-progress-${row.id}`} style={{ fontSize: 'var(--text-caption)', color: 'var(--action-primary)' }}>
          {push.progress?.text || 'Wysyłanie…'}
        </span>
      )}
      {push && push.status !== 'running' && !editMode && push.retried && (
        <span data-testid={`ceeb-push-retried-${row.id}`} style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
          Ponowiono automatycznie ({push.retried.label || 'stan przejściowy CEEB'})
        </span>
      )}
      {push?.status === 'done' && !editMode && (() => {
        // Zadanie 3.3/3.4: „Wysłano (0)" NIE jest sukcesem — zero oznaczonych lokali albo brak zapisu
        // wersji roboczej pokazujemy ostrzegawczo, z powodem (markReason) i listą lokali z problemem.
        const total = push.apartmentCount;
        const synced = push.synced ?? 0;
        const partial = push.saved === false || synced === 0 || (total != null && synced < total);
        const failed = (push.perApartment || []).filter((a) => !a.synced);
        return (
          <>
            <span style={{ fontSize: 'var(--text-caption)', color: partial ? 'var(--status-rec)' : 'var(--status-ok)', fontWeight: partial ? 'var(--weight-semibold)' : undefined }}>
              {push.saved === false ? 'NIE zapisano w CEEB' : `Wysłano ${synced}${total != null ? `/${total}` : ''} lokali`}
            </span>
            {partial && push.markReason && (
              <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>{push.markReason}</span>
            )}
            {failed.slice(0, 3).map((a) => (
              <span key={a.nr} style={{ fontSize: 'var(--text-caption)', color: 'var(--status-rec)' }}>
                Lokal {a.nr}: {a.reason}
              </span>
            ))}
            {failed.length > 3 && (
              <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>…i {failed.length - 3} więcej — szczegóły w raporcie bota</span>
            )}
            {(push.errors || []).slice(0, 2).map((e, i) => (
              <span key={`${e.step}-${i}`} style={{ fontSize: 'var(--text-caption)', color: 'var(--status-deny)' }}>{e.step}: {e.error}</span>
            ))}
          </>
        );
      })()}
      {push?.status === 'error' && !editMode && (
        <>
          <span style={{ fontSize: 'var(--text-caption)', color: 'var(--status-deny)', fontWeight: 'var(--weight-semibold)' }}>
            Błąd wysyłki
          </span>
          {push.error && (
            <span style={{ fontSize: 'var(--text-caption)', color: 'var(--status-deny)' }}>{push.error}</span>
          )}
          {(push.errors || []).slice(0, 2).map((e, i) => (
            <span key={`${e.step}-${i}`} style={{ fontSize: 'var(--text-caption)', color: 'var(--status-deny)' }}>{e.step}: {e.error}</span>
          ))}
        </>
      )}
      {/* Odmowa „Wyślij do CEEB" przy protokole już wystawionym → propozycja poprawki (superadmin, decyduje rodzic). */}
      {onEditIssued && (
        <button
          type="button"
          data-testid={`ceeb-edit-issued-offer-${row.id}`}
          onClick={onEditIssued}
          style={{
            padding: 0, border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left',
            font: 'inherit', fontSize: 'var(--text-caption)', fontWeight: 'var(--weight-semibold)',
            color: 'var(--status-rec)', textDecoration: 'underline',
          }}
        >
          Popraw wystawiony protokół w CEEB
        </button>
      )}
      {/* E2 Z4: tablet trzyma nowszą wersję lokali (409 TABLET_PENDING) — superadmin może świadomie wysłać wersję z serwera. */}
      {onTabletOverride && (
        <button
          type="button"
          data-testid={`ceeb-tablet-override-${row.id}`}
          onClick={onTabletOverride}
          style={{
            padding: 0, border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left',
            font: 'inherit', fontSize: 'var(--text-caption)', fontWeight: 'var(--weight-semibold)',
            color: 'var(--status-rec)', textDecoration: 'underline',
          }}
        >
          Wyślij mimo to (wersja z serwera)
        </button>
      )}
      {/* Sprzeczne dane budynku (2026-08-31). Backend liczył je i wysyłał na Telegram, ale panel
          ich nie pokazywał — a to MY wpisujemy te wartości do rejestru państwowego.
          Ostrzeżenie, nie blokada: `floors=1` przy wielu lokalach ma 796 z 1641 budynków lustra. */}
      {(push?.ostrzezeniaDanych || []).length > 0 && (
        <span
          data-testid={`ceeb-data-warn-${row.id}`}
          title={(push!.ostrzezeniaDanych || []).map((o) => `${o.pole}: ${o.problem}`).join('\n')}
          style={{ fontSize: 'var(--text-caption)', color: 'var(--changed-text)', fontWeight: 'var(--weight-semibold)' }}
        >
          ⚠ Dane budynku do sprawdzenia ({(push!.ostrzezeniaDanych || []).length})
        </span>
      )}
      {/* Lokale z „wspólne" zaznaczonym przez bota z reguły domyślnej (Bronek 2026-10-14) — operator widzi je
          przed finalizacją (zapis bez informacji o źródłach w budynku ze wspólnym C.O.). Informacja, nie błąd. */}
      {push?.defaultConnected && push.defaultConnected.nrs.length > 0 && (
        <span
          data-testid={`ceeb-default-connected-${row.id}`}
          style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}
        >
          ℹ {push.defaultConnected.message}
        </span>
      )}
      {/* Drugi protokół w tym roku (2026-10-10, D6): backend zapisał go tylko jako wersję roboczą —
          komunikat mówi, co sprawdzić w CEEB. Widoczny zawsze (także przy pełnym sukcesie i przy błędzie). */}
      {push?.notice && (
        <span
          data-testid={`ceeb-push-notice-${row.id}`}
          style={{ fontSize: 'var(--text-caption)', color: 'var(--changed-text)', fontWeight: 'var(--weight-semibold)' }}
        >
          ⚠ {push.notice}
        </span>
      )}
      {/* Finalizacja bez potwierdzenia w rejestrze (2026-09-01): bot kliknął „Zapisz", ale odczyt
          zwrotny tego nie potwierdził → `ceeb_status` NIE został ostemplowany. */}
      {push?.status === 'done' && push.statusConfirm && push.statusConfirm.stamp === false && (
        <span
          data-testid={`ceeb-finalize-unconfirmed-${row.id}`}
          style={{ fontSize: 'var(--text-caption)', color: 'var(--status-deny)', fontWeight: 'var(--weight-semibold)' }}
        >
          Finalizacja NIEPOTWIERDZONA — {push.statusConfirm.reason || 'brak odczytu'}
        </span>
      )}
      {/* Status PROTOKOŁU w CEEB (036/037) — osobny wymiar od liczników per-lokal wyżej.
          Dopiero on mówi, czy protokół jest wystawiony i czeka na zielony przycisk. */}
      {row.ceebStatus && (
        <span
          data-testid={`ceeb-protocol-status-${row.id}`}
          style={{
            fontSize: 'var(--text-caption)',
            fontWeight: 'var(--weight-semibold)',
            color: row.ceebStatus === 'Zaakceptowany' ? 'var(--status-ok)' : 'var(--changed-text)',
          }}
        >
          Protokół: {row.ceebStatus}
          {row.ceebCaseNumber ? ` (${row.ceebCaseNumber})` : ''}
          {row.ceebAcceptedAt ? ` — ${new Date(row.ceebAcceptedAt).toLocaleDateString('pl-PL')}` : ''}
        </span>
      )}
      {/* 074 (właściciel 2026-10-21): protokół zniknął z CEEB (usunięty) — nie da się go przywrócić; nową wersję
          roboczą zakłada następne „Wyślij do CEEB”. Znacznik znika, gdy w CEEB pojawi się nowy protokół. */}
      {row.ceebDeletedProtocol && (
        <span
          data-testid={`ceeb-protocol-deleted-${row.id}`}
          title={ceebDeletedFull(row.ceebDeletedProtocol.caseNumber, row.ceebDeletedProtocol.detectedAt)}
          style={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--weight-semibold)', color: 'var(--status-deny)' }}
        >
          {ceebDeletedShort(row.ceebDeletedProtocol.caseNumber, row.ceebDeletedProtocol.detectedAt)}
        </span>
      )}
    </Box>
  );
}

// Faza 1 (2026-09-02, #243): baner „Lustro CEEB" — czy dane w tym widoku są świeże. Nocny skan
// (cron 05:15, `ceebApiImport.js --report`) zostawia raport; do 2026-09-02 był niewidoczny i awaria
// loginu z 1.09 przeszła bez sygnału. Jednorazowy odczyt przy montażu (bez pollingu — skan biegnie raz
// na dobę); błąd odczytu = brak banera, nie błąd widoku; `idle` (skan z raportem jeszcze nie biegł) = nic.
function CeebNightlyBanner() {
  const [st, setSt] = useState<CeebNightlyImportStatus | null>(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await buildingsApi.nightlyImportStatus();
        if (alive) setSt(r);
      } catch {
        if (alive) setSt(null);
      }
    })();
    return () => { alive = false; };
  }, []);
  if (!st || st.status === 'idle') return null;
  const kiedy = st.finishedAt
    ? `${new Date(st.finishedAt).toLocaleDateString('pl-PL')} ${new Date(st.finishedAt).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}`
    : '?';
  const zle = st.status === 'error' || st.ok === false;
  const bledy = st.errorCount || 0;
  const tresc = zle
    ? `Nocny skan CEEB nieudany (${kiedy}): ${st.error || 'bez opisu'} — dane w tym widoku mogą być nieaktualne.`
    : `Lustro CEEB: ostatni skan ${kiedy} · ${st.protocols ?? 0} protokołów · nowe budynki: ${st.createdBuildings ?? 0} · nowe protokoły: ${st.newProtocols ?? 0}${(st.recordsInserted ?? 0) > 0 ? ` · zapisy lokali z CEEB: ${st.recordsInserted}` : ''}${(st.inspectionsCreated ?? 0) > 0 ? ` · przeglądy założone z CEEB: ${st.inspectionsCreated}` : ''}${bledy ? ` · błędy: ${bledy}` : ''}${(st.flueCountsMissing ?? 0) > 0 ? ` · zakres kontroli do uzupełnienia w CEEB: ${st.flueCountsMissing}` : ''}`;
  const adresy = !zle && Array.isArray(st.createdAddresses) ? st.createdAddresses : [];
  // 074: protokoły nowo wykryte jako usunięte w CEEB tej nocy (pełna lista w podpowiedzi).
  const usuniete = !zle && st.ghostProtocols && Array.isArray(st.ghostProtocols.marked) ? st.ghostProtocols.marked : [];
  return (
    <div
      data-testid="ceeb-nightly-banner"
      role={zle ? 'alert' : 'status'}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-3) var(--space-4)',
        border: `var(--border-width) solid ${zle ? 'var(--status-deny-bar)' : (bledy ? 'var(--status-warn-bar, var(--border-strong))' : 'var(--border-strong)')}`,
        borderRadius: 'var(--radius-md)',
        background: zle ? 'var(--status-deny-bg)' : 'var(--surface-card)',
        color: zle ? 'var(--status-deny)' : 'var(--text-secondary)',
        fontSize: 'var(--text-label)',
      }}
    >
      <Icon name={zle ? 'triangle-alert' : 'refresh'} size={18} />
      <span style={{ flex: 1 }}>{tresc}</span>
      {adresy.length > 0 && (
        <span data-testid="ceeb-nightly-new" title={adresy.join('\n')} style={{ whiteSpace: 'nowrap', color: 'var(--text-primary)' }}>
          {adresy.slice(0, 2).join(', ')}{adresy.length > 2 ? ', …' : ''}
        </span>
      )}
      {usuniete.length > 0 && (
        <span
          data-testid="ceeb-nightly-deleted"
          title={usuniete.map((m) => `${m.address || '?'}: ${m.caseNumber}`).join('\n')}
          style={{ whiteSpace: 'nowrap', color: 'var(--status-deny)' }}
        >
          Usunięte w CEEB: {usuniete.length} ({usuniete.slice(0, 2).map((m) => `${m.address || '?'} ${m.caseNumber}`).join(', ')}{usuniete.length > 2 ? ', …' : ''})
        </span>
      )}
    </div>
  );
}

// D4 (2026-10-22): baner „Wysyłki do CEEB — ostatnia doba" obok „Lustro CEEB" — wynik, czas i powód każdej wysyłki /
// finalizacji / akceptacji z audytu (GET /api/ceeb/push-report). Jednorazowy odczyt przy montażu (jak nocny skan); błąd
// odczytu albo brak przebiegów = brak banera. Lista szczegółów rozwijana (`details`), bez nowego modala.
function CeebPushReportBanner() {
  const [r, setR] = useState<CeebPushReport | null>(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const x = await buildingsApi.pushReport();
        if (alive) setR(x);
      } catch {
        if (alive) setR(null);
      }
    })();
    return () => { alive = false; };
  }, []);
  if (!r || !r.total) return null;
  const c = r.counts;
  const zle = c.error + c.partial + c.unknown;
  const ikona: Record<string, string> = { ok: '✓', partial: '⚠', error: '✗', running: '…', unknown: '?' };
  return (
    <details
      data-testid="ceeb-push-report-banner"
      style={{
        padding: 'var(--space-3) var(--space-4)',
        border: `var(--border-width) solid ${zle ? 'var(--status-warn-bar, var(--border-strong))' : 'var(--border-strong)'}`,
        borderRadius: 'var(--radius-md)',
        background: 'var(--surface-card)',
        color: 'var(--text-secondary)',
        fontSize: 'var(--text-label)',
      }}
    >
      <summary style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <Icon name={zle ? 'triangle-alert' : 'send'} size={18} />
        <span data-testid="ceeb-push-report-summary">
          Wysyłki do CEEB — ostatnia doba: {r.total} · OK {c.ok}{c.partial ? ` · częściowo ${c.partial}` : ''}
          {c.error ? ` · błąd ${c.error}` : ''}{c.running ? ` · w toku ${c.running}` : ''}{c.unknown ? ` · bez wyniku ${c.unknown}` : ''}
          {r.retried ? ` · ponowione automatycznie ${r.retried}` : ''}
        </span>
      </summary>
      <ul style={{ margin: 'var(--space-2) 0 0', paddingLeft: 'var(--space-5)', display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {r.items.map((i) => (
          <li
            key={`${i.kind}-${i.jobId}`}
            data-testid={`ceeb-push-report-item-${i.jobId}`}
            style={{ color: i.result === 'error' ? 'var(--status-deny)' : (i.result === 'partial' ? 'var(--status-rec)' : 'var(--text-secondary)') }}
          >
            {ikona[i.result] || '•'} {i.finishedAt || i.startedAt
              ? new Date((i.finishedAt || i.startedAt) as string).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' }) : '—'}
            {' '}{i.address || '(bez adresu)'} — {i.kindLabel}: {i.reason}
            {i.durationMin != null ? ` · ${String(i.durationMin).replace('.', ',')} min` : ''}
            {i.retried ? ` (ponowiono: ${i.retried.label || i.retried.reason})` : ''}
          </li>
        ))}
      </ul>
    </details>
  );
}

interface PipelineViewProps {
  /** Prefiltr etapu na wejściu (drill-down z Pulpitu, #10). null/brak = grupa „Aktywne". */
  initialStage?: LifecycleState | null;
}

/**
 * 2026-10-15 (Bronek): po „Zaplanuj" obawa, że planowanie „działa na istniejących protokołach".
 * Nie działa — zapisuje tylko terminy u nas; nowy protokół bieżącego roku powstaje dopiero przy
 * „Wyślij do CEEB" (create-first, protokołów z lat poprzednich bot nie edytuje).
 */
function PlanningCeebNote({ testId }: { testId: string }) {
  return (
    <Box data-testid={testId} sx={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
      Planowanie zapisuje tylko terminy w aplikacji — nic nie zmienia w CEEB. Nowy protokół{' '}
      {new Date().getFullYear()} powstanie przy „Wyślij do CEEB”.
    </Box>
  );
}

const PipelineView: React.FC<PipelineViewProps> = ({ initialStage = null }) => {
  const [rows, setRows] = useState<PipelineBuilding[]>([]);
  const [counts, setCounts] = useState<Record<LifecycleState, number> | null>(null);
  // Liczba budynków pasujących do frazy we WSZYSTKICH etapach (z /pipeline/counts).
  const [countsTotal, setCountsTotal] = useState(0);
  const [pendingCeeb, setPendingCeeb] = useState(0);

  // stage = konkretny chip (wygrywa z grupą); null => grupa „Aktywne".
  // Stan bazy → etap UI (W_TERENIE → ZAPLANOWANY, DO_POWROTU → GOTOWY_DO_CEEB): chip musi się
  // zaświecić także wtedy, gdy ktoś poda prefiltr stanem, który nie ma własnego chipa.
  const [stage, setStage] = useState<LifecycleState | null>(initialStage ? stageOf(initialStage) : null);
  // Szukanie PONAD etapami (group='all'). Domyślnie false — lista trzyma się chipa.
  // Włącza je wyłącznie „Pokaż wszystkie" z pustego stanu: bez tego kominiarz szukający
  // budynku spoza etapu „Aktywne" widział „Brak wyników" i zakładał duplikat (QA 2026-08-05).
  const [allStages, setAllStages] = useState(false);
  // feat/bulk-reschedule-unplan: chip „Po terminie” — zaległe (I termin minął, 0 zapisów) w etapach Zaplanowany/W terenie (budynki zamknięte przez CEEB pomijamy).
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [overdueCount, setOverdueCount] = useState(0);
  // Migr. 073 (właściciel 21.10, „Zaplanuj do kolejki”): chip „W kolejce (N)” — tylko kolejka; etapy i „Aktywne” jej nie
  // pokazują (filtr backendu), więc ich liczniki pomniejszamy o `queued_counts`. „Do kolejki”/„Usuń z kolejki” — okno.
  const [queuedOnly, setQueuedOnly] = useState(false);
  const [queuedCount, setQueuedCount] = useState(0);
  const [queuedCounts, setQueuedCounts] = useState<Partial<Record<LifecycleState, number>>>({});
  const [queueTargets, setQueueTargets] = useState<{ mode: 'add' | 'remove'; targets: PipelineBuilding[] } | null>(null);
  // 074: chip „Usunięte w CEEB” — budynki, których protokół zniknął z CEEB (licznik = ten sam WHERE co lista).
  const [ceebDeletedOnly, setCeebDeletedOnly] = useState(false);
  const [ceebDeletedCount, setCeebDeletedCount] = useState(0);
  // B1 (Bronek 21.10, „osobna kategoria dla budynków jednorodzinnych”): chip „Jednorodzinne” — budynki jednolokalowe
  // ze wszystkich etapów (filtr przekrojowy jak „Usunięte w CEEB”, nie etap: w chipach etapów zostają jak były).
  const [singleFamilyOnly, setSingleFamilyOnly] = useState(false);
  const [singleFamilyCount, setSingleFamilyCount] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  // E2: filtr zarządcy ('' = wszyscy). `clients` ładujemy RAZ na mount i karmimy nimi
  // zarówno ten dropdown, jak i modal paczki wg filtrów (E4) — jedno źródło, jeden fetch.
  const [clientId, setClientId] = useState('');
  const [clients, setClients] = useState<Client[]>([]);
  // 068 (C3): filtr po kategorii zarządcy ('' = wszystkie, 'none' = bez zarządcy / bez kategorii).
  // Lista kategorii wynika z już pobranych zarządców (bez osobnego zapytania) — w filtrze są tylko
  // kategorie, które mają jakiegoś zarządcę.
  const [clientCategoryId, setClientCategoryId] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<PipelineSort>('ceeb_activity');
  // 2026-09-14 (właściciel): sortowanie w obie strony — drugi klik w nagłówek odwraca, przełącznik przy „Sortuj”.
  const [sortDir, setSortDir] = useState<PipelineSortDir>(SORT_DEFAULT_DIR.ceeb_activity);
  const changeSort = (next: PipelineSort) => { setSort(next); setSortDir(SORT_DEFAULT_DIR[next]); setPage(0); };
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Wymiar CEEB — stan wysyłki per budynek (polling status-file backendu).
  const [ceebPush, setCeebPush] = useState<Record<string, PushState>>({});
  // G12 (2026-10-19): wysyłki trwające wg serwera (status-file'y) — po odświeżeniu panelu albo w innej przeglądarce
  // wiersz też pokazuje „Wysyłanie…". Dociągane razem z listą (loadRows); `attachedRunJobs` = przebiegi już podpięte
  // pod polling (każdy co najwyżej raz — nieaktualna lista nie wznowi zakończonego przebiegu).
  const [serverRunning, setServerRunning] = useState<CeebPushRunningResponse['running']>([]);
  const attachedRunJobs = useRef<Set<string>>(new Set());
  // 036: ZIELONY przycisk „Akceptuj w CEEB" — wyłącznie superadmin (żądanie właściciela
  // 2026-07-24). Widoczny, gdy protokół jest w CEEB „Wprowadzony" (bot zapisał na czysto)
  // i nie został jeszcze zaakceptowany. Akcja NIEODWRACALNA → CeebAcceptModal pyta serwer
  // o bramkę (2026-10-09: lokale z terenu poza CEEB = Kawika 12/14/16) i dopiero wtedy pozwala kliknąć.
  const auth = useAuthOptional();
  const isSuperadmin = auth?.user?.role === 'superadmin';
  // A11: import harmonogramu — konto kominiarza (sweep) go nie widzi; serwer i tak odpowie 403.
  const canImportSchedule = auth?.user?.role !== 'sweep';
  // „Popraw wystawiony protokół w CEEB" (2026-10-09) stoi za przełącznikiem serwera CEEB_EDIT_ISSUED, domyślnie
  // WYŁĄCZONYM — wdrożenie kodu nie zmienia panelu. Jednorazowy odczyt (trasa tylko dla superadmina); błąd = wyłączone.
  const [editIssuedFlag, setEditIssuedFlag] = useState(false);
  useEffect(() => {
    if (!isSuperadmin) { setEditIssuedFlag(false); return undefined; }
    let alive = true;
    (async () => {
      try {
        const flags = await ceebFlagsApi.get();
        if (alive) setEditIssuedFlag(flags?.CEEB_EDIT_ISSUED === true);
      } catch {
        // Brak odczytu = akcja niewidoczna (kierunek bezpieczny); serwer i tak odmówiłby EDIT_ISSUED_DISABLED.
        if (alive) setEditIssuedFlag(false);
      }
    })();
    return () => { alive = false; };
  }, [isSuperadmin]);
  const canEditIssuedRole = isSuperadmin && editIssuedFlag;
  const [acceptTarget, setAcceptTarget] = useState<CeebAcceptTarget | null>(null);
  const [acceptOutcome, setAcceptOutcome] = useState<(CeebAcceptOutcome & { address: string }) | null>(null);
  // refresh jest deklarowany niżej — ref pozwala wywołać go z handlera bez zmiany kolejności.
  const refreshRef = useRef<null | (() => void)>(null);
  const acceptAddressRef = useRef<Record<string, string>>({});
  const acceptJob = useCeebAcceptPolling((o) => {
    setAcceptOutcome({ ...o, address: acceptAddressRef.current[o.buildingId] || '' });
    refreshRef.current?.();
  });
  const confirmAccept = (buildingId: string, opts: { acknowledgePending: boolean }) => {
    acceptAddressRef.current[buildingId] = acceptTarget?.address || '';
    setAcceptTarget(null);
    void acceptJob.start(buildingId, opts);
  };

  const pollTimers = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  // Raport bota CEEB (#51b — follow-up #51/B7: raport gotowy, ale niewpięty w żywe UI).
  // Po świeżym terminalu [Wyślij do CEEB] próbujemy dociągnąć last-meta.json; hook sam
  // filtruje po inspectionId (plik jest globalny/nadpisywany), więc modal pokazuje się
  // TYLKO gdy meta faktycznie dotyczy WŁAŚNIE wysłanego budynku — inaczej wystarczy status
  // w kolumnie „Status CEEB" (CeebStatusCell), który działa niezależnie od tego raportu.
  const [ceebReportBuilding, setCeebReportBuilding] = useState<PipelineBuilding | null>(null);
  const { meta: ceebReportMeta } = useLastCeebMeta(ceebReportBuilding?.inspectionId ?? null);

  // [Otwórz] — podgląd budynku/przeglądu. inspectionId=null → BIV w trybie read-only (W6).
  // row trzymamy do callbacku „Zaplanuj przegląd" z read-only BIV.
  const [openBuilding, setOpenBuilding] = useState<{ buildingId: string; inspectionId: string | null; row?: PipelineBuilding } | null>(null);

  // Modal [Zaplanuj] (NOWY → nowy przegląd).
  const [planFor, setPlanFor] = useState<PipelineBuilding | null>(null);
  const [planForm, setPlanForm] = useState({ firstTermDate: '', secondTermDate: '', assignedTo: '', apartmentCount: '' });
  // A12: zarządca (+ e-mail, gdy go nie ma) i liczba lokali — opcjonalne, zapis po udanym planowaniu.
  const [planClient, setPlanClient] = useState<PlanClientValue>(EMPTY_PLAN_CLIENT);
  // 078: protokół CEEB, z którego import bierze dane przeglądu ('' = bieżący, jak dotąd).
  const [planSource, setPlanSource] = useState<string>(PREFILL_SOURCE_DEFAULT);
  // Wynik pojedynczego planowania (lokale / zarządca) — baner nad listą; okno po sukcesie się zamyka.
  const [planNotice, setPlanNotice] = useState<{ kind: 'error' | 'info'; text: string } | null>(null);
  // fix/term-date-validation: rok 0002 z <input type=date> (zgłoszenie 08.09) — min/max + komunikat,
  // przycisk wyłączony, gdy którakolwiek data jest spoza zakresu (backend i tak odrzuci 400).
  const termBounds = termDateBounds();
  const planDateErr = termDateError(planForm.firstTermDate) || termDateError(planForm.secondTermDate);
  const planAptErr = apartmentCountError(planForm.apartmentCount);
  const [planSaving, setPlanSaving] = useState(false);
  // #52 (B8) — 409 (guard duplikatu #46): budynek ma już aktywną inspekcję. existingInspectionId
  // brak tylko gdy backend nie zwrócił kształtu (np. body nie-JSON) — wtedy sam komunikat, bez akcji.
  const [planConflict, setPlanConflict] = useState<{
    buildingId: string;
    message: string;
    existingInspectionId?: string;
  } | null>(null);

  // Modal [Zaplanuj II termin] (apt_open>0 i brak II terminu → additional-terms, slot 2).
  const [termFor, setTermFor] = useState<PipelineBuilding | null>(null);
  const [termForm, setTermForm] = useState({ date: '', reason: '' });
  const [termSaving, setTermSaving] = useState(false);

  // Multi-select + zbiorcze planowanie (właściciel 2026-07-09). DataGrid v8: model selekcji
  // to { type:'include'|'exclude', ids:Set } (breaking vs v7 tablica). keepNonExistentRowsSelected
  // pozwala zbierać zaznaczenia między stronami (paginacja server-side). Obsługujemy głównie
  // tryb 'include' (konkretne UUID); 'exclude' (nagłówkowe „zaznacz wszystkie") liczymy wobec
  // widocznej strony — świadomy kompromis, bo enumeracja wszystkich ID serwera byłaby kosztowna.
  const [selection, setSelection] = useState<GridRowSelectionModel>({ type: 'include', ids: new Set() });
  // Wysyłka ZBIORCZA (2026-09-01). Hook = kanon pollingu; inline `setInterval` w
  // `handlePushToCeeb` jest zastanym długiem, nie wzorcem do naśladowania.
  const batchJob = useCeebBatchPolling(PUSH_BATCH_API);
  const [batchConfirm, setBatchConfirm] = useState(false);
  // Akceptacja ZBIORCZA (2026-10-09, Bronek: „zaznaczyłem trzy budynki"). Sekwencyjnie po stronie
  // serwera; budynki z lokalami z terenu poza CEEB idą WYŁĄCZNIE pojedynczo (świadome obejście).
  const acceptBatch = useCeebBatchPolling(ACCEPT_BATCH_API);
  const [acceptBatchConfirm, setAcceptBatchConfirm] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ firstTermDate: '', secondTermDate: '', assignedTo: '' });
  const bulkDateErr = termDateError(bulkForm.firstTermDate) || termDateError(bulkForm.secondTermDate);
  const [bulkClient, setBulkClient] = useState<PlanClientValue>(EMPTY_PLAN_CLIENT);
  // 078: rok protokołu CEEB jako źródło danych ('' = bieżący) — protokół z roku wybiera serwer per budynek.
  const [bulkSource, setBulkSource] = useState<string>(PREFILL_SOURCE_DEFAULT);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkResult, setBulkResult] = useState<{
    planned: string[]; rescheduled: string[]; skipped: string[]; errors: { address: string; message: string }[];
    // A12: wynik przypisania zarządcy (gdy wybrany w oknie).
    client?: PlanClientOutcome | null; clientError?: string;
    // 078: budynki bez protokołu z wybranego roku (dane z bieżącego) / błąd zapisu źródła.
    sourceNotes?: { address: string; message: string }[];
  } | null>(null);
  // A11: „Importuj harmonogram (Excel)".
  const [importOpen, setImportOpen] = useState(false);
  // E1: hurtowy ZIP z podsumowaniami „Nieprawidłowości" dla zaznaczenia.
  const [zipBusy, setZipBusy] = useState(false);
  // E5 (§8): hurtowe pobieranie oficjalnych PDF-ów protokołów z CEEB (job + polling + zapis ZIP-a).
  // Wspólne z kompletem w widoku budynku (B10) — useProtocolPdfsPack.
  const pdfPack = useProtocolPdfsPack((msg) => setZipMsg(msg));
  const pdfJob = pdfPack.job;

  // Źródła ciepła w podsumowaniach (Bronek 14.10) — JEDNA opcja panelu dla kompletu, ZIP-a podsumowań
  // i paczki wg filtrów (wspólna ze szczegółami budynku). Domyślnie bez: paczki idą do zarządców.
  const [summaryHeatSources] = useSummaryHeatSources();

  // #416 (lista Bronka 12.10): `complete` = komplet do wysyłki — ten sam job, a w ZIP-ie katalog
  // na budynek z protokołami CEEB, podsumowaniem i kartami lokali z podpisami.
  const downloadProtocolPdfs = async (complete = false) => {
    const ids = selectedBuildingIds();
    if (!ids.length || pdfJob.status === 'running') return;
    setZipMsg(null);
    await pdfPack.start(ids, pdfYear, complete);
  };
  // E4: paczka podsumowań wybierana FILTRAMI (bez zaznaczania wierszy).
  const [packOpen, setPackOpen] = useState(false);
  const [packForm, setPackForm] = useState({
    clientId: '', dateFrom: '', dateTo: '', protocolYear: '',
    // E6: typ budynku + co pakujemy (domyslnie samo podsumowanie, jak przed E6).
    buildingType: '', includeFindings: true, includeProtocols: false,
  });
  // E6: rocznik dla hurtowych protokolow CEEB ('' = wszystkie lata).
  // Właściciel 2026-10-02 (docx „Panel Administrator"): domyślnie BIEŻĄCY rok — „wszystkie lata"
  // ściągało całą historię protokołów, a zwykle potrzebny jest tylko tegoroczny.
  const [pdfYear, setPdfYear] = useState(currentProtocolYear);
  const [packError, setPackError] = useState<string | null>(null);
  const [zipMsg, setZipMsg] = useState<{ kind: 'error' | 'info'; text: string } | null>(null);

  const [addOpen, setAddOpen] = useState(false);

  // Właściciel 2026-09-13 (pkt 24): [Odplanuj] dla ZAPLANOWANY/W_TERENIE — DELETE przeglądu, budynek wraca
  // do NOWY (trigger 018). Backend odmawia 409, gdy są zapisy lokali / protokół w CEEB — błąd
  // zostaje w modalu, żeby operator go przeczytał (nie alert()).
  const [unplanFor, setUnplanFor] = useState<PipelineBuilding | null>(null);
  const [unplanBusy, setUnplanBusy] = useState(false);
  const [unplanError, setUnplanError] = useState<string | null>(null);
  const openUnplan = (b: PipelineBuilding) => { setUnplanError(null); setUnplanFor(b); };
  const closeUnplan = () => { if (!unplanBusy) { setUnplanFor(null); setUnplanError(null); } };
  const confirmUnplan = async () => {
    if (!unplanFor || !unplanFor.inspectionId) return;
    setUnplanBusy(true);
    setUnplanError(null);
    try {
      await inspectionsApi.delete(unplanFor.inspectionId);
      setUnplanFor(null);
      refreshRef.current?.();
    } catch (e) {
      setUnplanError(e instanceof Error ? e.message : 'Nie udało się odplanować przeglądu');
    } finally {
      setUnplanBusy(false);
    }
  };

  // Właściciel 2026-09-13 (pkt 26): nowy budynek jest NOWY (modal nie zakłada już przeglądu) —
  // po dodaniu przełączamy listę na etap NOWY i wpisujemy adres w szukajkę, żeby był od razu
  // widoczny z przyciskiem [Zaplanuj]. Bez tego ginąłby wśród ~3700 budynków NOWY.
  const handleCreated = (created: { id: string; address: string }) => {
    setStage('NOWY');
    setAllStages(false);
    setSearchInput(created.address);
    setSearch(created.address);
    setPage(0);
    refreshRef.current?.();
  };

  // Dokładny duplikat w „Dodaj budynek": zamiast tworzyć kopię pokazujemy istniejący wpis na liście
  // (wszystkie etapy — istniejący budynek może być w dowolnym, adres w szukajce). Zdejmujemy też filtr
  // zarządcy i „Po terminie" — inaczej lista mogłaby wyjść pusta i użytkownik utworzyłby kopię.
  const handleShowExisting = (existing: { id: string; address: string }) => {
    setAddOpen(false);
    setStage(null);
    setAllStages(true);
    setOverdueOnly(false);
    setQueuedOnly(false);
    setCeebDeletedOnly(false);
    setClientId('');
    setClientCategoryId('');
    setSearchInput(existing.address);
    setSearch(existing.address);
    setPage(0);
  };

  // Debounce szukajki (server-side, po search_blob pg_trgm) → reset strony.
  // Ostatnia fraza zastosowana przez debounce — żeby odróżnić „użytkownik wyczyścił szukajkę" od „szukajka pusta od wejścia".
  const ostatniaFraza = useRef('');
  useEffect(() => {
    const t = setTimeout(() => {
      const next = searchInput.trim();
      setSearch(next);
      // Wyczyszczenie szukajki kończy tryb „wszystkie etapy" — inaczej po skasowaniu frazy
      // lista pokazałaby wszystkie 1600+ budynków zamiast wrócić do „Aktywnych".
      // 2026-10-09 (przegląd kodu): TYLKO przy przejściu fraza → pusto. Debounce po montażu zerował `allStages`
      // ustawione chipem „Po terminie" klikniętym w ciągu 300 ms (lista z overdue=true, ale group='active').
      if (!next && ostatniaFraza.current) setAllStages(false);
      ostatniaFraza.current = next;
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Lista zarządców do filtra — jednorazowo; jej błąd nie może zabrać listy budynków,
  // więc degraduje do pustego dropdowna („— wszyscy —" zostaje).
  useEffect(() => {
    let alive = true;
    clientsApi.getAll()
      .then((cs) => { if (alive) setClients(cs); })
      .catch(() => { if (alive) setClients([]); });
    return () => { alive = false; };
  }, []);

  // 068 (C3): kategorie z listy zarządców (unikalne, alfabetycznie) + zarządcy zawężeni do wybranej kategorii.
  const clientCategories = useMemo(() => {
    const byId = new Map<string, string>();
    for (const c of clients) if (c.categoryId && c.categoryName) byId.set(c.categoryId, c.categoryName);
    return Array.from(byId, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, 'pl'));
  }, [clients]);
  const clientsInCategory = useMemo(() => {
    if (!clientCategoryId) return clients;
    if (clientCategoryId === 'none') return clients.filter((c) => !c.categoryId);
    return clients.filter((c) => c.categoryId === clientCategoryId);
  }, [clients, clientCategoryId]);

  const loadCounts = useCallback(async () => {
    try {
      const res = await buildingsApi.pipelineCounts(search || undefined, clientId || undefined, clientCategoryId || undefined);
      setCounts(res.counts);
      setPendingCeeb(res.pending_ceeb);
      setOverdueCount(res.overdue ?? 0);
      setQueuedCount(res.queued ?? 0);
      setQueuedCounts(res.queued_counts ?? {});
      setCeebDeletedCount(res.ceeb_deleted ?? 0);
      setSingleFamilyCount(res.single_family ?? 0);
      // `total` liczy WSZYSTKIE etapy dla bieżącej frazy — to z niego bierze się podpowiedź
      // „znaleziono N w innych etapach". Endpoint i tak leci przy każdej zmianie frazy,
      // więc podpowiedź nie kosztuje dodatkowego zapytania.
      setCountsTotal(res.total);
    } catch {
      // liczniki nie blokują listy — cicho.
    }
  }, [search, clientId, clientCategoryId]);

  // G12: trwające wysyłki — osobno od listy (nie blokuje jej i nie psuje jej błędem).
  const loadServerRunning = useCallback(async () => {
    try {
      const r = await buildingsApi.ceebPushRunning();
      setServerRunning(Array.isArray(r?.running) ? r.running : []);
    } catch (e) {
      // Brak odczytu = lista jak dotąd (stan „w toku" tylko z tej karty); kolumna „Status CEEB" i liczniki działają dalej.
      console.warn('[PipelineView] trwające wysyłki CEEB niedostępne:', e);
    }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    void loadServerRunning();
    try {
      const res = await buildingsApi.pipeline({
        // allStages (klik „Pokaż wszystkie") zdejmuje ZARÓWNO chip, jak i grupę „Aktywne" —
        // sam stage=null dałby wciąż group='active', czyli nadal nie pokazałby etapu „Nowy".
        stage: allStages ? undefined : (stage ?? undefined),
        group: allStages ? 'all' : (stage ? undefined : 'active'),
        search: search || undefined,
        sort,
        dir: sortDir,
        // DataGrid trzyma stronę 0-based, backend pipelineQuery jest 1-based
        // (page<1 → 1, offset=(page-1)*pageSize) — bez +1 strony 1 i 2 są identyczne (#24).
        page: page + 1,
        pageSize,
        clientId: clientId || undefined,
        clientCategoryId: clientCategoryId || undefined,
        overdue: overdueOnly || undefined,
        queued: queuedOnly || undefined,
        ceebDeleted: ceebDeletedOnly || undefined,
        singleFamily: singleFamilyOnly || undefined,
      });
      setRows(res.data);
      setTotal(res.total);
    } catch (e: any) {
      setLoadError(e?.message || 'Nie udało się wczytać listy budynków');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [stage, allStages, search, sort, sortDir, page, pageSize, clientId, clientCategoryId, overdueOnly, queuedOnly, ceebDeletedOnly, singleFamilyOnly, loadServerRunning]);

  useEffect(() => { loadRows(); }, [loadRows]);

  // 2026-09-15 (właściciel): zmiany z tabletów mają być widoczne bez ręcznego odświeżania —
  // powrót do karty panelu (visibilitychange) dociąga listę i liczniki, nie częściej niż co 60 s.
  const lastVisibleRefreshRef = useRef(0);
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastVisibleRefreshRef.current < 60_000) return;
      lastVisibleRefreshRef.current = now;
      loadRows();
      loadCounts();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loadRows, loadCounts]);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  // Sprzątanie pollingu na unmount.
  useEffect(() => {
    const timers = pollTimers.current;
    return () => {
      Object.values(timers).forEach((t) => clearInterval(t));
    };
  }, []);

  const refresh = useCallback(() => { loadRows(); loadCounts(); }, [loadRows, loadCounts]);
  refreshRef.current = refresh;

  const selectStage = (s: LifecycleState | null) => {
    setOverdueOnly(false);
    setQueuedOnly(false);
    setCeebDeletedOnly(false);
    setSingleFamilyOnly(false);
    setStage(s);
    // Klik w chip zawsze wraca do filtrowania po etapie — inaczej chip świeciłby się
    // jako aktywny, a lista pokazywałaby wszystko.
    setAllStages(false);
    setPage(0);
  };

  // „Pokaż wszystkie (N)" z pustego stanu — zdejmuje filtr etapu, zostawia frazę.
  // Chip „Po terminie”: zaległe bez filtra etapu (stage=null, group=all; backend ogranicza do ZAPLANOWANY/W_TERENIE) + filtr overdue.
  const selectOverdue = () => {
    const next = !overdueOnly;
    setOverdueOnly(next);
    setQueuedOnly(false);
    setCeebDeletedOnly(false);
    setSingleFamilyOnly(false);
    setStage(null);
    setAllStages(next);
    setPage(0);
  };
  // Migr. 073: chip „W kolejce” — jak „Po terminie”: bez filtra etapu (group=all), backend zwraca tylko kolejkę.
  const selectQueued = () => {
    const next = !queuedOnly;
    setQueuedOnly(next);
    setOverdueOnly(false);
    setCeebDeletedOnly(false);
    setSingleFamilyOnly(false);
    setStage(null);
    setAllStages(next);
    setPage(0);
  };
  // 074: chip „Usunięte w CEEB” — jak „Po terminie”: bez filtra etapu (group=all), backend zwraca tylko takie budynki.
  const selectCeebDeleted = () => {
    const next = !ceebDeletedOnly;
    setCeebDeletedOnly(next);
    setOverdueOnly(false);
    setQueuedOnly(false);
    setSingleFamilyOnly(false);
    setStage(null);
    setAllStages(next);
    setPage(0);
  };
  // B1: chip „Jednorodzinne” — jak „Usunięte w CEEB”: bez filtra etapu (group=all), backend zwraca tylko type=jednolokalowy.
  const selectSingleFamily = () => {
    const next = !singleFamilyOnly;
    setSingleFamilyOnly(next);
    setOverdueOnly(false);
    setQueuedOnly(false);
    setCeebDeletedOnly(false);
    setStage(null);
    setAllStages(next);
    setPage(0);
  };
  const showAllStages = () => {
    setQueuedOnly(false);
    setCeebDeletedOnly(false);
    setSingleFamilyOnly(false);
    setAllStages(true);
    setStage(null);
    setPage(0);
  };

  const stopPoll = (id: string) => {
    const t = pollTimers.current[id];
    if (t) { clearInterval(t); delete pollTimers.current[id]; }
  };

  // pkt 14: potwierdzenie przed wysyłką, gdy „Zakres kontroli" w CEEB jest pusty (wysłać można).
  const [flueConfirmFor, setFlueConfirmFor] = useState<PipelineBuilding | null>(null);
  // E2 Z4: superadmin potwierdza wysyłkę wersji z serwera, choć tablet trzyma nowszą (409 TABLET_PENDING).
  const [tabletOverrideFor, setTabletOverrideFor] = useState<PipelineBuilding | null>(null);
  // 2026-10-21 (Prusa 28): potwierdzenie „Wyślij do CEEB z nieobecnościami".
  const [inaccessibleConfirmFor, setInaccessibleConfirmFor] = useState<PipelineBuilding | null>(null);
  // „Finalizuj w CEEB" (właściciel 2026-09-15): gotowy draft wpisany ręcznie w CEEB — potwierdzenie,
  // bo wystawienie protokołu jest nieodwracalne; bot tylko przeklikuje „Wyślij" + „Zapisz".
  const [finalizeConfirmFor, setFinalizeConfirmFor] = useState<PipelineBuilding | null>(null);
  // „Popraw wystawiony protokół w CEEB" (superadmin, 2026-10-09) — modal z bramką serwera i kopią przed zmianą.
  const [editIssuedFor, setEditIssuedFor] = useState<PipelineBuilding | null>(null);
  // #404 (D6): przy drugim protokole w roku „Finalizuj" wymaga świadomego potwierdzenia (pole wyboru w oknie).
  const [finalizeSecondAck, setFinalizeSecondAck] = useState(false);
  useEffect(() => { setFinalizeSecondAck(false); }, [finalizeConfirmFor]);

  // Polling status-file'u wysyłki/finalizacji (wspólny dla [Wyślij do CEEB] i [Finalizuj w CEEB]) i poprawki
  // wystawionego protokołu (`mode: 'edit-issued'`). `jobId` (gdy znany) odsiewa stary terminal poprzedniego przebiegu.
  const pollCeebJob = useCallback((b: PipelineBuilding, jobId?: string) => {
    stopPoll(b.id);
    pollTimers.current[b.id] = setInterval(async () => {
      try {
        const st = await buildingsApi.ceebPushStatus(b.id);
        if (jobId && st.jobId && st.jobId !== jobId) return;
        if (st.status === 'done') {
          stopPoll(b.id);
          setCeebPush((p) => ({ ...p, [b.id]: {
            status: 'done', synced: st.synced, apartmentCount: st.apartmentCount, saved: st.saved,
            markReason: st.markReason, perApartment: st.perApartment, errors: st.errors,
            ostrzezeniaDanych: st.ostrzezeniaDanych, statusConfirm: st.statusConfirm, notice: st.notice,
            defaultConnected: st.defaultConnected, retried: st.retried || null,
            ...editIssuedFields(st, p[b.id]),
          } }));
          setCeebReportBuilding(b); // spróbuj pokazać raport bota (#51b) — dopasowanie po inspectionId w hooku
          refresh();
        } else if (st.status === 'error') {
          stopPoll(b.id);
          setCeebPush((p) => ({
            ...p,
            [b.id]: { status: 'error', error: st.error, errors: st.errors, ostrzezeniaDanych: st.ostrzezeniaDanych, notice: st.notice, retried: st.retried || null, ...editIssuedFields(st, p[b.id]) },
          }));
          // BACKUP_FAILED = bot nie wystartował; last-meta.json tego przeglądu to raport STAREGO przebiegu — nie pokazujemy.
          if (st.code !== 'BACKUP_FAILED') setCeebReportBuilding(b);
        } else if (st.status === 'running' && st.mode === 'edit-issued') {
          // Poprawka ma kroki (kopia → bot → kopia po zmianie) — pokazujemy bieżący.
          setCeebPush((p) => ({ ...p, [b.id]: { status: 'running', ...editIssuedFields(st, p[b.id]) } }));
        } else if (st.status === 'running') {
          // D1: postęp z serwera (ten sam polling — bez nowego timera).
          setCeebPush((p) => ({ ...p, [b.id]: { ...(p[b.id] || {}), status: 'running', progress: st.progress || null } }));
        }
      } catch {
        // przejściowy błąd pollingu — próbuj dalej.
      }
    }, 2500);
  }, [refresh]);

  // G12: wysyłka trwająca na serwerze (start z innej karty/przeglądarki albo sprzed odświeżenia) — wiersz na bieżącej
  // stronie dostaje „Wysyłanie…"/„Poprawianie…" i TEN SAM polling co po kliknięciu (jobId odsiewa inny przebieg).
  // Pomijamy budynek, który ta karta już poluje (własne kliknięcie).
  useEffect(() => {
    serverRunning.forEach((r) => {
      const key = r.jobId || `${r.buildingId}:${r.startedAt || ''}`;
      if (attachedRunJobs.current.has(key) || pollTimers.current[r.buildingId]) return;
      const row = rows.find((x) => x.id === r.buildingId);
      if (!row) return;
      attachedRunJobs.current.add(key);
      setCeebPush((p) => ({
        ...p,
        [r.buildingId]: { status: 'running', progress: r.progress || null, ...(r.mode === 'edit-issued' ? { mode: 'edit-issued' } : {}) },
      }));
      pollCeebJob(row, r.jobId || undefined);
    });
  }, [serverRunning, rows, pollCeebJob]);

  // [Wyślij do CEEB] — domyślnie z finalizacją (⇒ „Wprowadzony”; pasek zbiorczy = tylko wersje robocze), per-lokal inkrementalnie (§1a). Spawn + polling.
  const handlePushToCeeb = useCallback(async (
    b: PipelineBuilding,
    opts: { skipFlueWarning?: boolean; ignoreTabletPending?: boolean } = {},
  ) => {
    if (b.flue_counts_missing && !opts.skipFlueWarning) {
      setFlueConfirmFor(b);
      return;
    }
    setCeebReportBuilding(null); // nowy push — zamknij ewentualny stary raport (#51b)
    setCeebPush((p) => ({ ...p, [b.id]: { status: 'running' } }));
    try {
      if (opts.ignoreTabletPending) await buildingsApi.pushToCeeb(b.id, { ignoreTabletPending: true });
      else await buildingsApi.pushToCeeb(b.id);
    } catch (e: any) {
      setCeebPush((p) => ({ ...p, [b.id]: { status: 'error', error: e?.message || 'Błąd wysyłki', code: e?.code, next: e?.next, retry: 'push' } }));
      return;
    }
    pollCeebJob(b);
  }, [pollCeebJob]);

  // [Wyślij do CEEB z nieobecnościami] (2026-10-21) — po potwierdzeniu; serwer zawsze zapisuje wersję roboczą.
  const handlePushInaccessible = useCallback(async (b: PipelineBuilding) => {
    setInaccessibleConfirmFor(null);
    setCeebReportBuilding(null);
    setCeebPush((p) => ({ ...p, [b.id]: { status: 'running' } }));
    try {
      await buildingsApi.pushToCeeb(b.id, { includeInaccessible: true });
    } catch (e: any) {
      setCeebPush((p) => ({ ...p, [b.id]: { status: 'error', error: e?.message || 'Błąd wysyłki', code: e?.code, next: e?.next, retry: 'inaccessible' } }));
      return;
    }
    pollCeebJob(b);
  }, [pollCeebJob]);

  // 202 z poprawki wystawionego protokołu → ten sam status-file co wysyłka; wiersz pokazuje „Poprawianie…".
  const handleEditIssuedStarted = useCallback((b: PipelineBuilding, jobId: string, dryRun: boolean) => {
    setEditIssuedFor(null);
    setCeebReportBuilding(null);
    setCeebPush((p) => ({ ...p, [b.id]: { status: 'running', mode: 'edit-issued', dryRun } }));
    pollCeebJob(b, jobId);
  }, [pollCeebJob]);

  // [Finalizuj w CEEB] — po potwierdzeniu; bot nie wypełnia pól, tylko „Wyślij" + „Zapisz".
  const handleFinalizeInCeeb = useCallback(async (b: PipelineBuilding, opts: { ignoreTabletPending?: boolean } = {}) => {
    setFinalizeConfirmFor(null);
    setCeebReportBuilding(null);
    setCeebPush((p) => ({ ...p, [b.id]: { status: 'running' } }));
    try {
      if (opts.ignoreTabletPending) await buildingsApi.finalizeInCeeb(b.id, { ignoreTabletPending: true });
      else await buildingsApi.finalizeInCeeb(b.id);
    } catch (e: any) {
      setCeebPush((p) => ({ ...p, [b.id]: { status: 'error', error: e?.message || 'Błąd finalizacji', code: e?.code, next: e?.next, retry: 'finalize' } }));
      return;
    }
    pollCeebJob(b);
  }, [pollCeebJob]);

  const handleOpen = (b: PipelineBuilding) => {
    // W6 — „Otwórz" ZAWSZE otwiera widok budynku, także bez przeglądu (inspectionId=null →
    // BIV w trybie read-only z hintem „Zaplanuj przegląd"). row = do callbacku planowania.
    setOpenBuilding({ buildingId: b.id, inspectionId: b.inspectionId, row: b });
  };

  const openPlan = (b: PipelineBuilding) => {
    setPlanFor(b);
    setPlanForm({ firstTermDate: '', secondTermDate: '', assignedTo: '', apartmentCount: '' });
    setPlanClient(EMPTY_PLAN_CLIENT);
    setPlanSource(PREFILL_SOURCE_DEFAULT);
    setPlanConflict(null);
  };

  const closePlan = () => {
    setPlanFor(null);
    setPlanConflict(null);
  };

  const savePlan = async () => {
    if (!planFor || !planForm.firstTermDate || planAptErr || planClientError(planClient, clients)) return;
    setPlanSaving(true);
    setPlanConflict(null);
    const target = planFor;
    // A12: liczba lokali tylko dla budynku bez lokali (pole ukryte, gdy lokale są; serwer i tak sprawdza).
    const aptCount = target.apt_total === 0 && !isQueued(target) && planForm.apartmentCount.trim()
      ? Number(planForm.apartmentCount.trim()) : undefined;
    try {
      // Migr. 073: budynek z kolejki MA już przegląd (bez terminów) — planowanie wpisuje terminy w ten przegląd (PUT;
      // backend nie traktuje wypełnienia pustych terminów jak przesunięcia). Gdy kominiarz zdążył zacząć (I termin
      // ustawiony z tabletu), backend odpowie 409 ACTIVE_INSPECTION_TERMS — komunikat zostaje w oknie.
      const created = isQueued(target) ? await inspectionsApi.update(target.inspectionId as string, {
        firstTermDate: planForm.firstTermDate,
        ...(planForm.secondTermDate ? { secondTermDate: planForm.secondTermDate } : {}),
        ...(planForm.assignedTo ? { assignedTo: planForm.assignedTo } : {}),
      } as any) as any : await inspectionsApi.create({
        buildingId: target.id,
        status: 'PLANNED',
        firstTermDate: planForm.firstTermDate || undefined,
        secondTermDate: planForm.secondTermDate || undefined,
        assignedTo: planForm.assignedTo || undefined,
        notes: undefined,
        isInvoiced: false,
        ...(aptCount ? { apartmentCount: aptCount } : {}),
        ...(planSource ? { prefillSourceCase: planSource } : {}),
      } as any);
      setPlanFor(null);
      // A12: zarządca po zapisie przeglądu — w oknie pojedynczym zmiana obecnego jest widoczna, więc reassign.
      const notes: string[] = [`Zaplanowano: ${target.address}.`];
      if (created.apartmentsAdded && created.apartmentsAdded.length > 0) notes.push(`Utworzono ${lokaleLabel(created.apartmentsAdded.length)}.`);
      else if (aptCount && created.apartmentsNote) notes.push(`Lokale: ${created.apartmentsNote}.`);
      let kind: 'info' | 'error' = 'info';
      // 078: przegląd z kolejki już istnieje — źródło danych osobnym zapisem (błąd nie cofa terminów).
      let sourceSaved = !!planSource;
      let sourceKept: string[] = [];
      if (planSource && isQueued(target)) {
        try {
          const src = await inspectionsApi.setPrefillSource(target.inspectionId as string, { caseNumber: planSource });
          sourceKept = src.prefillFieldsAlreadySet || [];
        } catch (se: any) {
          kind = 'error';
          sourceSaved = false;
          notes.push(`Nie udało się zapisać protokołu CEEB jako źródła danych: ${se?.message || 'błąd serwera'} — terminy są zapisane.`);
        }
      }
      if (sourceSaved) notes.push(`Dane z protokołu CEEB ${planSource} wczytają się przy najbliższym imporcie.`);
      // Przegląd z kolejki mógł już dostać prefill z poprzedniego źródła — import nie nadpisuje wypełnionych pól.
      if (sourceKept.length) notes.push(`Już wypełnione w przeglądzie (zostają): ${sourceKept.join(', ')} — popraw je w widoku budynku.`);
      // Ten sam zarządca co obecny też przechodzi — wpisany e-mail ma się zapisać; attach to wtedy no-op.
      if (planClient.clientId) {
        try {
          notes.push(planClientSummary(await savePlanClient(planClient, clients, [target.id], { reassign: true })));
          clientsApi.getAll().then(setClients).catch(() => { /* lista zarządców odświeży się przy następnym wejściu */ });
        } catch (ce: any) {
          kind = 'error';
          notes.push(`Nie udało się przypisać zarządcy: ${ce?.message || 'błąd serwera'} — przegląd jest zapisany, zarządcę ustaw w „Przypisz zarządcę”.`);
        }
      }
      if (notes.length > 1 || kind === 'error') setPlanNotice({ kind, text: notes.join(' ') });
      refresh();
    } catch (e: any) {
      // #52 (B8) — 409 (guard duplikatu #46 w backend): budynek ma już aktywną (nie-COMPLETED)
      // inspekcję. Zamiast generycznego alert()/crasha: komunikat tonem warn (DS tokeny) +
      // akcja „Przejdź do istniejącego" (gdy backend podał existingInspectionId), używająca
      // tego samego mechanizmu podglądu co przycisk [Otwórz] (setOpenBuilding).
      const conflict = e as InspectionConflictError;
      if (isQueued(target) && conflict?.status === 409) {
        setPlanConflict({
          buildingId: target.id,
          existingInspectionId: target.inspectionId || undefined,
          message: 'Kominiarz zaczął już ten przegląd w terenie (I termin ustawił się z tabletu) — zmianę terminów zrób w widoku budynku.',
        });
      } else if (conflict?.status === 409) {
        const label = conflict.existingStatus ? INSPECTION_STATUS_LABELS[conflict.existingStatus] : null;
        setPlanConflict({
          buildingId: planFor.id,
          existingInspectionId: conflict.existingInspectionId,
          message: label
            ? `Budynek ma już aktywny przegląd (status: ${label}). Otwórz istniejący zamiast tworzyć nowy.`
            : 'Budynek ma już aktywny przegląd. Otwórz istniejący zamiast tworzyć nowy.',
        });
      } else {
        alert(e?.message || 'Nie udało się zaplanować przeglądu');
      }
    } finally {
      setPlanSaving(false);
    }
  };

  // #52 (B8) — z banera konfliktu 409: zamknij modal [Zaplanuj] i otwórz istniejącą inspekcję
  // (ten sam widok co przycisk [Otwórz] w wierszu tabeli).
  const goToExistingInspection = () => {
    if (!planConflict?.existingInspectionId) return;
    setOpenBuilding({ buildingId: planConflict.buildingId, inspectionId: planConflict.existingInspectionId });
    setPlanFor(null);
    setPlanConflict(null);
  };

  // Etykieta przycisku/okna planowania kolejnego terminu — numeruje BACKEND (mapPipelineRow).
  const termButtonLabel = (b: PipelineBuilding) => {
    const roman = romanTerm(b.nextTermNumber ?? null);
    return roman ? `Zaplanuj ${roman} termin` : 'Zaplanuj kolejny termin';
  };

  const openSecondTerm = (b: PipelineBuilding) => {
    setTermFor(b);
    setTermForm({ date: '', reason: '' });
  };

  const saveSecondTerm = async () => {
    if (!termFor || !termFor.inspectionId || !termForm.date) return;
    setTermSaving(true);
    try {
      await inspectionsApi.addAdditionalTerm(termFor.inspectionId, {
        date: termForm.date,
        reason: termForm.reason || undefined,
      });
      setTermFor(null);
      refresh();
    } catch (e: any) {
      alert(e?.message || 'Nie udało się dodać terminu');
    } finally {
      setTermSaving(false);
    }
  };

  // --- Zbiorcze planowanie (multi-select) -----------------------------------
  // Efektywne ID zaznaczonych budynków z modelu selekcji DataGrid v8.
  const selectedBuildingIds = (): string[] => {
    if (selection.type === 'exclude') {
      return rows.filter((r) => !selection.ids.has(r.id)).map((r) => r.id);
    }
    return Array.from(selection.ids).map((id) => String(id));
  };
  const selectedCount = selection.type === 'exclude'
    ? Math.max(0, rows.length - selection.ids.size)
    : selection.ids.size;
  const addressOf = (id: string): string => rows.find((r) => r.id === id)?.address || id;
  /**
   * Zaznaczone budynki, które FAKTYCZNIE mają co wysłać. Backend i tak odpowie 409 na budynek
   * bez lokali w kolejce — filtrujemy wcześniej, żeby licznik na przycisku nie kłamał.
   */
  const doWyslaniaIds = (): string[] => selectedBuildingIds()
    .filter((id) => {
      const r = rows.find((x) => x.id === id);
      return !!r && r.apt_pending_ceeb > 0 && !protocolIssued(r);
    });
  /** Zaznaczone z protokołem „Wprowadzony" czekającym na akceptację (przycisk widać tylko przy takich). */
  const czekajaNaAkceptacjeIds = (): string[] => selectedBuildingIds()
    .filter((id) => { const r = rows.find((x) => x.id === id); return !!r && awaitsAcceptance(r); });
  /** …z nich te, które przejdą bramkę partii: wszystkie lokale z terenu w CEEB. */
  const doAkceptacjiIds = (): string[] => czekajaNaAkceptacjeIds()
    .filter((id) => (rows.find((x) => x.id === id)?.apt_pending_ceeb ?? 0) === 0);
  /**
   * pkt 14 (audyt 2026-09-30): wysyłka zbiorcza ostrzega tak samo jak pojedyncza — które z
   * wysyłanych budynków mają pusty „Zakres kontroli" w CEEB (`flue_counts_missing` z listy).
   */
  const flueMissingIds = (): string[] => doWyslaniaIds()
    .filter((id) => rows.find((r) => r.id === id)?.flue_counts_missing === true);

  const clearSelection = () => setSelection({ type: 'include', ids: new Set() });

  // --- E1: hurtowe podsumowania „Nieprawidłowości" (ZIP) ---------------------
  // Decyzja właściciela 2026-08-04 (§8): jeden ZIP, w środku OSOBNY plik per adres.
  // Zaznaczenie bierzemy z tego samego `selectedBuildingIds()`, co zbiorcze planowanie —
  // przeżywa paginację tak samo jak ono.
  /**
   * Wspólna ścieżka pobrania: zaznaczenie (tablica id) ALBO filtry (obiekt). `filename` to
   * nazwa ZAPASOWA (gdy serwer nie poda Content-Disposition); `forceFilename` = nazwa klienta
   * wygrywa zawsze (paczka wg filtrów E4 — nazwa niesie kryteria, których backend nie zna).
   */
  const runSummariesZip = async (
    selection: string[] | SummariesZipFilters,
    filename: string,
    onError: (msg: string) => void,
    forceFilename = false,
    appendix = false,
  ) => {
    if (zipBusy) return false;
    setZipBusy(true);
    setZipMsg(null);
    try {
      // Opcje przekazujemy tylko włączone — domyślne body bez zmian. Źródła ciepła: wspólna opcja panelu.
      const opts = {
        ...(appendix ? { appendix: true } : {}),
        ...(summaryHeatSources ? { heatSources: true } : {}),
      };
      const { blob, skipped, filename: serverName } = Object.keys(opts).length
        ? await buildingsApi.downloadSummariesZip(selection, opts)
        : await buildingsApi.downloadSummariesZip(selection);
      saveBlob(blob, (!forceFilename && serverName) || filename);
      if (skipped > 0) {
        setZipMsg({
          kind: 'info',
          text: `Pobrano paczkę. ${budynkiLabel(skipped)} pominięto (brak dostępu lub budynek nie istnieje) `
              + '— szczegóły w pliku pominiete.txt w archiwum.',
        });
      }
      return true;
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Nie udało się pobrać podsumowań');
      return false;
    } finally {
      setZipBusy(false);
    }
  };

  // Dodatek do podsumowań w ZIP-ie zaznaczenia (kontrakt #338) — domyślnie wyłączony.
  const [zipAppendix, setZipAppendix] = useState(false);
  const downloadSummariesZip = async () => {
    const ids = selectedBuildingIds();
    if (!ids.length) return;
    await runSummariesZip(ids, 'podsumowania.zip', (text) => setZipMsg({ kind: 'error', text }), false, zipAppendix);
  };

  // Kontrakt #326 (właściciel: „podsumowanie per budynek 1, 2, 3, 4"): zestawienie źródeł ciepła
  // zaznaczonych budynków — snapshot id w chwili kliknięcia, żeby zmiana zaznaczenia nie przeliczała modala.
  const [hsSummaryIds, setHsSummaryIds] = useState<string[] | null>(null);
  // „Niesprawdzone lokale" dla zaznaczonych (właściciel 2026-10-02, docx: „eksport / udostępnienie
  // większej ilości budynków") — modal z listą + CSV/schowek.
  const [uncheckedIds, setUncheckedIds] = useState<string[] | null>(null);

  // „Pobierz ▾" + „?" w pasku zaznaczenia (właściciel 2026-10-02, docx: „Przyciski na górze – do wytłumaczenia").
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const [bulkHelpOpen, setBulkHelpOpen] = useState(false);
  const downloadMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!downloadMenuOpen) return undefined;
    const onDown = (e: MouseEvent) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target as Node)) setDownloadMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDownloadMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [downloadMenuOpen]);
  // Pasek znika razem z zaznaczeniem — panel nie może „czekać otwarty" na następne zaznaczenie.
  useEffect(() => { if (selectedCount === 0) setDownloadMenuOpen(false); }, [selectedCount]);

  // „Przypisz zarządcę" dla zaznaczonych (właściciel 2026-10-02, docx: „z poziomu zakładki BUDYNKI
  // łatwiej byłoby przyporządkować / przerzucać"). Krok 1: attach-buildings przypina budynki BEZ
  // zarządcy (te same reguły co w Zarządcach, PR #312). Krok 2: budynki z INNYM zarządcą wracają
  // w `skipped` → ConfirmModal z listą „teraz: X" → attach z reassign:true. Bez potwierdzenia nic
  // cudzego się nie przepina.
  const [assignIds, setAssignIds] = useState<string[] | null>(null);
  const [assignTarget, setAssignTarget] = useState('');
  const [assignBusy, setAssignBusy] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [reassignAsk, setReassignAsk] = useState<{
    targetId: string; attached: number; others: { id: string; address: string; clientName: string | null }[];
  } | null>(null);
  const targetName = (id: string) => {
    const c = clients.find((x) => x.id === id);
    return c ? (c.shortName || c.name) : 'wybrany zarządca';
  };
  const openAssign = () => {
    setAssignIds(selectedBuildingIds());
    setAssignTarget('');
    setAssignError(null);
  };
  const submitAssign = async () => {
    if (!assignIds || !assignTarget) return;
    setAssignBusy(true);
    setAssignError(null);
    try {
      const res = await clientsApi.attachBuildings(assignTarget, { buildingIds: assignIds });
      const others = res.skipped
        .filter((x) => x.reason === 'other-client')
        .map((x) => ({
          id: x.id,
          address: `${[x.street, x.houseNumber].filter(Boolean).join(' ')}${x.city ? `, ${x.city}` : ''}`,
          clientName: x.clientName,
        }));
      setAssignIds(null);
      if (others.length > 0) {
        setReassignAsk({ targetId: assignTarget, attached: res.attached, others });
      } else {
        setZipMsg({ kind: 'info', text: `Przypisano do: ${targetName(assignTarget)} — ${budynkiLabel(res.attached)}` });
      }
      refresh();
    } catch (e: any) {
      setAssignError(e?.message || 'Nie udało się przypisać zarządcy');
    } finally {
      setAssignBusy(false);
    }
  };
  // Komunikat po pytaniu „Zmienić zarządcę?": baner paska zaznaczenia, a gdy zaznaczenia już nie ma (pytanie po
  // „Zaplanuj dla zaznaczonych", A12) — baner nad listą, żeby wynik nie przepadł w niewidocznym pasku.
  const notifyAssign = (msg: { kind: 'error' | 'info'; text: string }) => {
    if (selectedCount > 0) setZipMsg(msg);
    else setPlanNotice(msg);
  };
  const confirmReassign = async () => {
    const ask = reassignAsk;
    if (!ask) return;
    setReassignAsk(null);
    try {
      const res = await clientsApi.attachBuildings(ask.targetId, { buildingIds: ask.others.map((o) => o.id), reassign: true });
      notifyAssign({
        kind: 'info',
        text: `Przypisano do: ${targetName(ask.targetId)} — ${budynkiLabel(ask.attached + res.attached)} (w tym zmieniony zarządca: ${res.reassigned ?? res.attached})`,
      });
    } catch (e: any) {
      notifyAssign({ kind: 'error', text: e?.message || 'Nie udało się zmienić zarządcy' });
    }
    refresh();
  };
  const skipReassign = () => {
    const ask = reassignAsk;
    setReassignAsk(null);
    if (ask) {
      notifyAssign({
        kind: 'info',
        text: `Przypisano do: ${targetName(ask.targetId)} — ${budynkiLabel(ask.attached)}; pominięto ${budynkiLabel(ask.others.length)} z innym zarządcą`,
      });
    }
  };

  // PROMPT 5 (Bronek 08.09/14.09): grafik zaznaczonych budynków do Excela — jeden wiersz = jeden
  // budynek, I i II termin w osobnych kolumnach (Bronek 15.09, scheduleXlsx.js). Zaznaczony budynek bez terminu
  // nie ma wiersza w pliku — backend liczy je w X-Skipped-Count, mówimy o tym wprost (także budynek „W kolejce” —
  // przegląd bez terminów, migr. 073).
  const [xlsxBusy, setXlsxBusy] = useState(false);
  const downloadScheduleXlsx = async () => {
    const ids = selectedBuildingIds();
    if (!ids.length || xlsxBusy) return;
    setXlsxBusy(true);
    setZipMsg(null);
    try {
      const { blob, skipped, filename } = await termsApi.exportXlsx({ buildingIds: ids });
      saveBlob(blob, filename);
      if (skipped > 0) {
        setZipMsg({ kind: 'info', text: `Pobrano grafik. Pominięto (bez terminu): ${budynkiLabel(skipped)}.` });
      }
    } catch (e) {
      setZipMsg({ kind: 'error', text: e instanceof Error ? e.message : 'Nie udało się pobrać grafiku' });
    } finally {
      setXlsxBusy(false);
    }
  };

  // A8 (lista Bronka 2026-10): „Wyślij grafik e-mailem" — ten sam plik co „Pobierz grafik", wysyła serwer
  // (mailer za flagą MAIL_ENABLED; przy wyłączonej modal pokazuje komunikat serwera). Snapshot id w chwili
  // kliknięcia. Adresat domyślnie = e-mail zarządcy, gdy WSZYSTKIE zaznaczone budynki (z bieżącej listy) mają
  // tego samego zarządcę z e-mailem; inaczej pole puste.
  const [scheduleMail, setScheduleMail] = useState<{ ids: string[]; defaultTo: string; label: string } | null>(null);
  const openScheduleMail = () => {
    const ids = selectedBuildingIds();
    if (!ids.length) return;
    const picked = ids.map((id) => rows.find((r) => r.id === id));
    const clientId = picked[0]?.clientId || null;
    const shared = clientId && picked.every((r) => r && r.clientId === clientId)
      ? clients.find((c) => c.id === clientId) : undefined;
    const email = shared?.email?.trim() || '';
    setScheduleMail({ ids, defaultTo: email, label: email && shared ? clientLabel(shared) : '' });
    setDownloadMenuOpen(false);
  };
  // A22 (lista Bronka 2026-10): „Numery protokołów" zaznaczonych — modal z listą, schowkiem i CSV.
  const [caseNumbersIds, setCaseNumbersIds] = useState<string[] | null>(null);

  // E4: paczka wg FILTRÓW — nie wymaga zaznaczania wierszy. Nazwa pliku niesie kryteria,
  // żeby w katalogu pobierania dało się odróżnić trzy paczki od siebie.
  const openPack = () => {
    setPackForm({
      clientId: '', dateFrom: '', dateTo: '', protocolYear: '',
      buildingType: '', includeFindings: true, includeProtocols: false,
    });
    setPackError(null);
    setPackOpen(true);
  };

  const packFilename = () => {
    // Bez polskich znaków i spacji — Content-Disposition i systemy plików bywają wrażliwe.
    const who = packForm.clientId
      ? asciiFilename(clients.find((c) => c.id === packForm.clientId)?.name || 'zarzadca')
      : 'wszyscy';
    const range = [packForm.dateFrom, packForm.dateTo].filter(Boolean).join('_')
      || (packForm.protocolYear ? `rok${packForm.protocolYear}` : 'wszystkie');
    // E6: nazwa mówi, co jest w środku (same protokoły vs komplet) i jakiego typu budynki.
    const what = packForm.includeProtocols && !packForm.includeFindings ? 'protokoly'
      : packForm.includeProtocols ? 'komplet' : 'podsumowania';
    const kind = packForm.buildingType ? `_${packForm.buildingType}` : '';
    return `${what}_${who}${kind}_${asciiFilename(range)}.zip`;
  };

  const submitPack = async () => {
    const filters: SummariesZipFilters = {};
    if (packForm.clientId) filters.clientId = packForm.clientId;
    if (packForm.dateFrom) filters.dateFrom = packForm.dateFrom;
    if (packForm.dateTo) filters.dateTo = packForm.dateTo;
    if (packForm.protocolYear) filters.protocolYear = Number(packForm.protocolYear);
    if (packForm.buildingType) filters.buildingType = packForm.buildingType;
    if (Object.keys(filters).length === 0) {
      setPackError('Podaj przynajmniej jeden filtr — inaczej paczka objęłaby wszystkie budynki.');
      return;
    }
    // E6: co ma być w paczce. Odznaczenie obu nie ma sensu — lepiej powiedzieć to wprost,
    // niż wysłać żądanie, które wróci pustym archiwum.
    const include: Array<'findings' | 'protocols'> = [];
    if (packForm.includeFindings) include.push('findings');
    if (packForm.includeProtocols) include.push('protocols');
    if (include.length === 0) {
      setPackError('Zaznacz przynajmniej jedną zawartość paczki: podsumowania albo protokoły lokali.');
      return;
    }
    filters.include = include;
    setPackError(null);
    const ok = await runSummariesZip(filters, packFilename(), setPackError, true);
    if (ok) setPackOpen(false);
  };

  const openBulkPlan = () => {
    setBulkForm({ firstTermDate: '', secondTermDate: '', assignedTo: '' });
    setBulkClient(EMPTY_PLAN_CLIENT);
    setBulkSource(PREFILL_SOURCE_DEFAULT);
    setBulkResult(null);
    setBulkOpen(true);
  };
  const closeBulkPlan = () => {
    // A12: budynki z INNYM zarządcą — to samo pytanie „Zmienić zarządcę?" co w „Przypisz zarządcę".
    const others = bulkResult?.client?.others || [];
    if (bulkResult?.client && others.length > 0) {
      setReassignAsk({ targetId: bulkClient.clientId, attached: bulkResult.client.attached, others });
    }
    setBulkOpen(false);
    setBulkResult(null);
  };

  // feat/bulk-reschedule-unplan (Bronek 08.09: „zbiorczo mam monit”): zaznaczenie dzieli się na
  // budynki BEZ otwartego przeglądu (POST jak dotąd) i Z otwartym (PUT z rescheduleActive:true —
  // ten sam guard B3 co „Edytuj przegląd”), gdy operator zostawi zaznaczone „Zmień też termin”.
  const [bulkReschedule, setBulkReschedule] = useState(true);
  const selectedWithOpenInspection = (): PipelineBuilding[] => selectedBuildingIds()
    .map((id) => rows.find((r) => r.id === id))
    .filter((r): r is PipelineBuilding => !!r && !!r.inspectionId && ACTIVE_STATES.includes(r.lifecycle_state));

  const runBulkPlan = async () => {
    const ids = selectedBuildingIds();
    if (!ids.length || !bulkForm.firstTermDate) return;
    setBulkSaving(true);
    const planned: string[] = [];
    const rescheduled: string[] = [];
    const skipped: string[] = [];
    const errors: { address: string; message: string }[] = [];
    const sourceNotes: { address: string; message: string }[] = [];
    const sourceYear = bulkSource ? Number(bulkSource) : null;
    const openIds = new Set(selectedWithOpenInspection().map((r) => r.id));
    // Migr. 073: z kolejki — terminy do istniejącego przeglądu bez terminów (zwykłe planowanie, nie przeplanowanie:
    // niezależne od „Zmień też termin”, bez rescheduleActive — gdy kominiarz zdążył zacząć, backend da 409 i budynek
    // trafia do błędów zamiast nadpisać I termin z tabletu).
    const queuedIds = new Set(selectedRows().filter(isQueued).map((r) => r.id));
    // Sekwencyjnie — guard 409 (budynek ma już aktywny przegląd) jest per-budynek;
    // taki budynek trafia do „pominięte", reszta i tak się zaplanuje. Bez zalewania backendu.
    for (const id of ids) {
      const addr = addressOf(id);
      if (queuedIds.has(id)) {
        const insp = rows.find((r) => r.id === id)?.inspectionId as string;
        try {
          await inspectionsApi.update(insp, {
            firstTermDate: bulkForm.firstTermDate,
            ...(bulkForm.secondTermDate ? { secondTermDate: bulkForm.secondTermDate } : {}),
            ...(bulkForm.assignedTo ? { assignedTo: bulkForm.assignedTo } : {}),
          } as any);
          planned.push(addr);
          // 078: przegląd z kolejki istnieje — rok źródła osobnym zapisem (błąd nie cofa terminu).
          if (sourceYear) {
            try {
              const src = await inspectionsApi.setPrefillSource(insp, { year: sourceYear });
              if (src.prefillSourceNote) sourceNotes.push({ address: addr, message: src.prefillSourceNote });
              const kept = src.prefillFieldsAlreadySet || [];
              if (src.changed && kept.length) sourceNotes.push({ address: addr, message: `już wypełnione (zostają): ${kept.join(', ')}` });
            } catch (se: any) {
              sourceNotes.push({ address: addr, message: `źródło danych nie zapisane: ${se?.message || 'błąd serwera'}` });
            }
          }
        } catch (e: any) {
          errors.push({
            address: addr,
            message: e?.status === 409
              ? 'kominiarz zaczął już przegląd w terenie — termin zmień w widoku budynku'
              : (e?.message || 'Nie udało się zaplanować'),
          });
        }
        continue;
      }
      if (openIds.has(id)) {
        if (!bulkReschedule) { skipped.push(addr); continue; }
        const insp = rows.find((r) => r.id === id)?.inspectionId as string;
        try {
          await inspectionsApi.update(insp, {
            firstTermDate: bulkForm.firstTermDate,
            // Puste „II termin” = bez zmian (COALESCE po stronie backendu), nie kasujemy.
            ...(bulkForm.secondTermDate ? { secondTermDate: bulkForm.secondTermDate } : {}),
            rescheduleActive: true,
          } as any);
          rescheduled.push(addr);
        } catch (e: any) {
          errors.push({ address: addr, message: e?.message || 'Nie udało się zmienić terminu' });
        }
        continue;
      }
      try {
        const created = await inspectionsApi.create({
          buildingId: id,
          status: 'PLANNED',
          firstTermDate: bulkForm.firstTermDate || undefined,
          secondTermDate: bulkForm.secondTermDate || undefined,
          assignedTo: bulkForm.assignedTo || undefined,
          notes: undefined,
          isInvoiced: false,
          ...(sourceYear ? { prefillSourceYear: sourceYear } : {}),
        } as any);
        planned.push(addr);
        if (created && created.prefillSourceNote) sourceNotes.push({ address: addr, message: created.prefillSourceNote });
      } catch (e: any) {
        const conflict = e as InspectionConflictError;
        if (conflict?.status === 409) skipped.push(addr);
        else errors.push({ address: addr, message: e?.message || 'Nie udało się zaplanować' });
      }
    }
    // A12: zarządca dla WSZYSTKICH zaznaczonych (także pominiętych — to osobna decyzja niż termin); budynki
    // z innym zarządcą nie są przepinane bez pytania (pytanie po zamknięciu wyniku, jak w „Przypisz zarządcę").
    let client: PlanClientOutcome | null = null;
    let clientError: string | undefined;
    if (bulkClient.clientId) {
      try {
        client = await savePlanClient(bulkClient, clients, ids);
        clientsApi.getAll().then(setClients).catch(() => { /* lista zarządców odświeży się przy następnym wejściu */ });
      } catch (ce: any) {
        clientError = ce?.message || 'Nie udało się przypisać zarządcy';
      }
    }
    setBulkSaving(false);
    setBulkResult({ planned, rescheduled, skipped, errors, client, clientError, sourceNotes });
    clearSelection();
    refresh();
  };

  // feat/bulk-reschedule-unplan: „Odplanuj zaznaczone (N)” — sekwencyjne DELETE z tymi samymi
  // blokadami 409 co przycisk w wierszu; wynik z powodem po polsku, bez alert()/window.confirm.
  const [bulkUnplanOpen, setBulkUnplanOpen] = useState(false);
  const [bulkUnplanBusy, setBulkUnplanBusy] = useState(false);
  const [bulkUnplanResult, setBulkUnplanResult] = useState<
    { unplanned: string[]; blocked: { address: string; reason: string }[]; errors: { address: string; message: string }[] } | null
  >(null);
  const unplannable = (): PipelineBuilding[] => selectedWithOpenInspection()
    .filter((r) => (r.lifecycle_state === 'ZAPLANOWANY' || r.lifecycle_state === 'W_TERENIE') && !isQueued(r));
  const BLOCK_REASONS: Record<string, string> = {
    INSPECTION_HAS_VISITS: 'są zapisane wizyty w lokalach',
    INSPECTION_IN_CEEB: 'protokół jest już w CEEB',
  };
  const runBulkUnplan = async () => {
    const targets = unplannable();
    if (!targets.length) return;
    setBulkUnplanBusy(true);
    const unplanned: string[] = [];
    const blocked: { address: string; reason: string }[] = [];
    const errors: { address: string; message: string }[] = [];
    for (const b of targets) {
      try {
        await inspectionsApi.delete(b.inspectionId as string);
        unplanned.push(b.address);
      } catch (e: any) {
        const code = e?.code as string | undefined;
        if (e?.status === 409 && code && BLOCK_REASONS[code]) blocked.push({ address: b.address, reason: BLOCK_REASONS[code] });
        else errors.push({ address: b.address, message: e?.message || 'Nie udało się odplanować' });
      }
    }
    setBulkUnplanBusy(false);
    setBulkUnplanResult({ unplanned, blocked, errors });
    clearSelection();
    refresh();
  };

  // Migr. 073: chipy etapów i „Aktywne” liczą to, co pokaże lista — bez kolejki (`counts` z backendu = wszystkie).
  const chipCounts: Record<LifecycleState, number> | null = counts
    ? (Object.fromEntries(Object.entries(counts).map(([st, n]) => [
      st, Math.max(0, (n || 0) - (queuedCounts[st as LifecycleState] || 0)),
    ])) as Record<LifecycleState, number>)
    : null;
  const activeCount = chipCounts
    ? ACTIVE_STATES.reduce((sum, s) => sum + (chipCounts[s] || 0), 0)
    : 0;
  // Zbiorcze akcje — tylko zaznaczone wiersze z bieżącej strony, które się kwalifikują.
  const selectedRows = (): PipelineBuilding[] => selectedBuildingIds()
    .map((id) => rows.find((r) => r.id === id))
    .filter((r): r is PipelineBuilding => !!r);
  // Migr. 073: zbiorcze „Do kolejki” (Nowy/Zamknięty bez przeglądu) i „Usuń z kolejki”.
  const queueable = (): PipelineBuilding[] => selectedRows().filter(canQueue);
  const unqueueable = (): PipelineBuilding[] => selectedRows().filter(isQueued);
  const afterQueueChange = () => { clearSelection(); refresh(); };

  // Czy fraza ma trafienia POZA bieżącym filtrem etapu? Tylko wtedy, gdy lista jest pusta —
  // przy niepustej liście nie ma czego podpowiadać (i tak nie dokładamy zapytania).
  const scopeLabel = stage ? `etapie „${LIFECYCLE_META[stage].label}"` : 'etapie „Aktywne"';
  const hasMatchesElsewhere = !!search && !allStages && total === 0 && countsTotal > 0;

  // Pusty stan listy — komunikat zależny od kontekstu filtra (szukajka / etap / grupa).
  const NoRowsOverlay = useCallback(() => {
    let title = 'Brak aktywnych budynków';
    let hint = 'Zmień filtr etapu albo dodaj nowy budynek.';
    if (search) {
      title = `Brak wyników dla „${search}"`;
      // Bez tej podpowiedzi „Sprawdź pisownię" sugerowało literówkę także wtedy, gdy budynek
      // JEST w bazie, tylko w innym etapie — i kominiarz zakładał nowy (QA 2026-08-05, S7).
      // Bez orzeczenia („pasuje"/„pasują") — inaczej odmiana rozjeżdża się przy 2–4.
      hint = hasMatchesElsewhere
        ? `Brak w ${scopeLabel} — w pozostałych etapach: ${budynkiLabel(countsTotal)}.`
        : 'Sprawdź pisownię lub wyczyść wyszukiwanie.';
    } else if (queuedOnly) {
      title = 'Kolejka jest pusta';
      hint = 'Budynek dodasz przyciskiem „Do kolejki” w etapie „Nowy” — w wierszu albo zbiorczo.';
    } else if (ceebDeletedOnly) {
      title = 'Brak budynków z protokołem usuniętym w CEEB';
      hint = 'Nocny skan CEEB nie wykrył zniknięcia żadnego protokołu bez następcy.';
    } else if (singleFamilyOnly) {
      title = 'Brak budynków jednorodzinnych';
      hint = 'Jednorodzinne = budynki typu „Jednolokalowy” (typ ustawia nocny import z CEEB albo okno „Dodaj budynek”).';
    } else if (stage) {
      title = `Brak budynków w etapie „${LIFECYCLE_META[stage].label}"`;
      hint = 'Wybierz inny etap na chipach powyżej.';
    }
    return (
      <Box
        data-testid="pipeline-empty"
        sx={{
          height: '100%',
          minHeight: 220,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-2)',
          p: 'var(--space-5)',
          textAlign: 'center',
        }}
      >
        <Icon name="building" size={40} color="var(--text-disabled)" />
        <span style={{ fontWeight: 'var(--weight-semibold)', color: 'var(--text-secondary)', fontSize: 'var(--text-body)' }}>
          {title}
        </span>
        <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>{hint}</span>
        {hasMatchesElsewhere && (
          <Button
            variant="secondary"
            icon="building"
            data-testid="pipeline-show-all-stages"
            onClick={showAllStages}
            style={{ marginTop: 'var(--space-2)' }}
          >
            {`Pokaż wszystkie (${countsTotal})`}
          </Button>
        )}
      </Box>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, stage, queuedOnly, ceebDeletedOnly, singleFamilyOnly, hasMatchesElsewhere, countsTotal, scopeLabel]);

  const columns: GridColDef[] = [
    {
      field: 'address',
      headerName: 'Adres',
      flex: 1.4,
      minWidth: 220,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        const b = params.row as PipelineBuilding;
        const ceebNote = b.ceeb_id ? ceebProtocolShortNote(b.ceeb_id) : null;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', py: 'var(--space-2)' }}>
            <span style={{ fontWeight: 'var(--weight-semibold)', color: 'var(--text-primary)', display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }}>
              {/* pkt 14 (2026-09-13): „Zakres kontroli" (liczby przewodów) pusty w CEEB — kominiarz
                  uzupełnia ręcznie w CEEB; nocny import przepisuje, wykrzyknik znika. */}
              {b.flue_counts_missing && <FlueWarningIcon testId={`flue-warning-${b.id}`} />}
              {b.address || '—'}
            </span>
            {/* Migr. 073: „W kolejce” — pod chipem „W kolejce” i w szukaniu we wszystkich etapach. */}
            <QueuedBadge row={b} />
            {/* „Oznaczenie specjalne" (§16, 2026-08-04) — nazwa własna budynku przy adresie. */}
            {b.special_marking && (
              <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
                {b.special_marking}
              </span>
            )}
            {/* 2026-10-15 (Bronek): numer sprawy z poprzedniego roku to OSTATNI protokół w CEEB —
                nowy powstanie przy „Wyślij do CEEB" (create-first). Dopisek + pełne wyjaśnienie w tooltipie. */}
            {b.ceeb_id && (
              <span
                style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}
                title={ceebNote ? ceebProtocolLabel(b.ceeb_id) : undefined}
              >
                CEEB: {b.ceeb_id}
                {ceebNote && ` (${ceebNote})`}
              </span>
            )}
          </Box>
        );
      },
    },
    {
      // 055 (właściciel 2026-10-02, docx): „kolumna ze skróconą nazwą zarządcy typu Complex / Avente /
      // ZBM / PGM Helenka / TDN". Skrót edytuje się w Zarządcach; bez skrótu — pełna nazwa (ucięta).
      field: 'client',
      headerName: 'Zarządca',
      width: 140,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const b = params.row as PipelineBuilding;
        const label = b.clientShortName || b.clientName;
        return label ? (
          <span
            title={b.clientName || undefined}
            style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-primary)' }}
          >
            {label}
          </span>
        ) : (
          <span style={{ color: 'var(--text-muted)' }}>—</span>
        );
      },
    },
    {
      // pkt 20/21 (2026-09-13): termin następnej kontroli wg CEEB (nocny import); czerwono, gdy minął.
      field: 'next_inspection_date',
      headerName: 'Termin nast. kontroli (CEEB)',
      width: 150,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        const d = (params.row as PipelineBuilding).next_inspection_date ?? null;
        const overdue = !!d && d < new Date().toISOString().slice(0, 10);
        return (
          <span style={{ color: overdue ? 'var(--status-deny-text)' : 'var(--text-primary)', fontWeight: overdue ? 'var(--weight-semibold)' : undefined }} title={overdue ? 'Termin z ostatniego protokołu CEEB minął — nowy protokół go odnowi' : undefined}>
            {formatTermDate(d)}
          </span>
        );
      },
    },
    {
      // pkt 13 (2026-09-14): ostatnia aktywność budynku w CEEB (najnowszy protokół / zmiana statusu w lustrze).
      field: 'ceeb_last_activity',
      headerName: 'Aktywność CEEB',
      width: 130,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => (
        <span title="Data założenia najnowszego protokołu albo ostatniej zmiany jego statusu w CEEB (wg nocnego importu)">
          {formatTermDate((params.row as PipelineBuilding).ceeb_last_activity ?? null)}
        </span>
      ),
    },
    {
      field: 'lifecycle_state',
      headerName: 'Etap',
      width: 190, // „Gotowy · 12 nieobecnych" w jednej linii
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <LifecycleChip
          state={(params.row as PipelineBuilding).lifecycle_state}
          aptOpen={(params.row as PipelineBuilding).apt_open}
        />
      ),
    },
    {
      field: 'readiness',
      headerName: 'Gotowość terenowa',
      // B7 (Bronek, Bytomska 98/100): „22/27 lokale" czytało się jak liczba lokali budynku — napis
      // mówi teraz wprost, co liczymy, a dymek (nagłówek i komórka) wyjaśnia obie liczby.
      description: READINESS_HINT,
      flex: 1,
      minWidth: 190,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        const b = params.row as PipelineBuilding;
        return (
          <Box sx={{ width: '100%', py: 'var(--space-2)' }} title={READINESS_HINT} data-testid={`readiness-${b.id}`}>
            <ProgressBar value={b.apt_terminal} max={b.apt_total} countText={doneOfApartmentsLabel(b.apt_terminal, b.apt_total)} />
          </Box>
        );
      },
    },
    {
      field: 'ceeb',
      headerName: 'Status CEEB',
      width: 170,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        const b = params.row as PipelineBuilding;
        const offer = canEditIssuedRole && offerEditIssuedAfterPush(b, ceebPush[b.id]);
        // Wysyłka z nieobecnościami nie ma obejścia bramki tabletu (serwer i tak odmawia) — bez przycisku „Wyślij mimo to".
        const tabletPending = isSuperadmin && ceebPush[b.id]?.status === 'error' && ceebPush[b.id]?.code === 'TABLET_PENDING'
          && ceebPush[b.id]?.retry !== 'inaccessible';
        return (
          <CeebStatusCell
            row={b}
            push={ceebPush[b.id]}
            onEditIssued={offer ? () => setEditIssuedFor(b) : undefined}
            onTabletOverride={tabletPending ? () => setTabletOverrideFor(b) : undefined}
          />
        );
      },
    },
    {
      // pkt 21/23 (2026-09-13): „Termin I" = plan; po starcie prac pokazuje datę rozpoczęcia
      // (pierwszy zapis lokalu z terenu). Kolumna „Przypisany" usunięta (zawsze pusta — decyzja właściciela).
      field: 'first_term',
      headerName: 'Termin I',
      width: 120,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        const b = params.row as PipelineBuilding;
        const started = b.started_at ?? null;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', py: 'var(--space-2)' }}>
            <span
              style={b.overdue ? { color: 'var(--status-deny)', fontWeight: 'var(--weight-semibold)' } : undefined}
              title={b.overdue ? 'Termin minął, nikt nie zapisał lokalu — zmień termin albo odplanuj' : undefined}
            >
              {formatTermDate(b.first_term_date ?? null)}
            </span>
            {started && (
              <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }} title="Data pierwszego zapisu z terenu">
                start {formatTermDate(started)}
              </span>
            )}
          </Box>
        );
      },
    },
    {
      field: 'second_term',
      headerName: 'Termin II',
      width: 110,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => (
        <span>{formatTermDate((params.row as PipelineBuilding).second_term_date ?? null)}</span>
      ),
    },
    {
      field: 'actions',
      headerName: 'Akcje',
      flex: 1.3,
      minWidth: 280,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const b = params.row as PipelineBuilding;
        const pushing = ceebPush[b.id]?.status === 'running';
        // [Finalizuj w CEEB] (właściciel 2026-09-15): budynek gotowy, wszystkie lokale już w CEEB
        // (apt_pending_ceeb = 0), protokół „Wersja robocza" z numerem sprawy.
        // #404 (D6): wstrzymany drugi protokół obok zaakceptowanego — budynek ZAMKNIETY, a finalizacja dozwolona.
        const doFinalizacji = (b.lifecycle_state === 'GOTOWY_DO_CEEB' || (b.lifecycle_state === 'ZAMKNIETY' && b.secondProtocolReleasable === true))
          && b.apt_pending_ceeb === 0 && b.ceebStatus === 'Wersja robocza' && !!b.ceebCaseNumber;
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', py: 'var(--space-2)' }}>
            {/* [Wyślij do CEEB] — niezależne od stanu; gdy są terminalne lokale poza CEEB (§1a).
                #365: także przy gotowym drafcie, który aplikacja już wysyłała — „Finalizuj" wymaga wtedy pola „Uwagi"
                potwierdzonego przez aplikację, a bez lokali do wysłania serwer robi WYŁĄCZNIE zapis wersji roboczej
                (nigdy finalizacji). Draft wpisany ręcznie w CEEB (ceebAppPushed=false) — bez zmian, tylko „Finalizuj".
                Nie przy protokole już wystawionym (2026-10-09): bot zmienia tylko wersje robocze, serwer
                odpowiada 409 PROTOCOL_ISSUED — takie lokale opisuje zielony przycisk akceptacji. */}
            {(b.apt_pending_ceeb > 0 || (doFinalizacji && b.ceebAppPushed === true)) && !protocolIssued(b) && (
              <Button
                variant="primary"
                icon="send"
                disabled={pushing}
                style={actionBtnStyle}
                title={b.apt_pending_ceeb > 0
                  ? 'Wpisze lokale do CEEB i wystawi protokół (status „Wprowadzony”) — potem zielony „Akceptuj w CEEB”. '
                    + 'Gdy bot zauważy coś do sprawdzenia, zostawi wersję roboczą i powie dlaczego.'
                  : 'Zapisze wersję roboczą i potwierdzi pole „Uwagi" — protokół nie zostanie wystawiony'}
                onClick={() => handlePushToCeeb(b)}
              >
                {pushing ? 'Wysyłanie…' : 'Wyślij do CEEB'}
              </Button>
            )}
            {/* [Wyślij do CEEB z nieobecnościami] (właściciel 2026-10-21, Prusa 28) — w terenie same nieobecności / odmowy
                lokatora (albo reszta już w CEEB): lokale idą wyłącznie zdaniem „nie udostępniono lokali nr.:…", zawsze
                wersja robocza. Okno potwierdzenia wylicza lokale i zdanie. */}
            {canPushInaccessible(b) && (
              <Button
                variant="secondary"
                icon="send"
                data-testid={`ceeb-push-inaccessible-${b.id}`}
                disabled={pushing}
                style={actionBtnStyle}
                title="Zapisze w CEEB wersję roboczą: lokale bez wejścia trafią do zdania „nie udostępniono lokali nr.:…” w „Uwagach” — protokół nie zostanie wystawiony"
                onClick={() => setInaccessibleConfirmFor(b)}
              >
                {pushing ? 'Wysyłanie…' : 'Wyślij do CEEB z nieobecnościami'}
              </Button>
            )}
            {/* [Finalizuj w CEEB] → bot tylko przeklikuje „Wyślij" + „Zapisz" ⇒ Wprowadzony (dopiero wtedy
                zielony „Akceptuj"). */}
            {doFinalizacji && (
              <Button
                variant="primary"
                icon="send"
                disabled={pushing}
                style={actionBtnStyle}
                title="Wystawi zapisaną wersję roboczą bez zmiany treści (status „Wprowadzony”) — potem zielony „Akceptuj w CEEB”"
                onClick={() => setFinalizeConfirmFor(b)}
              >
                {pushing ? 'Finalizowanie…' : 'Finalizuj w CEEB'}
              </Button>
            )}
            {/* [Akceptuj w CEEB] — ZIELONY, tylko superadmin, tylko gdy protokół „Wprowadzony"
                i jeszcze nie zaakceptowany (036, feedback właściciela 2026-07-24). Lokale z terenu poza
                CEEB (2026-10-09, Kawika 12/14/16) → przycisk ostrzegawczy; modal wymaga świadomego potwierdzenia. */}
            {isSuperadmin && awaitsAcceptance(b) && (
              b.apt_pending_ceeb > 0 ? (
                <Button
                  variant="secondary"
                  icon="triangle-alert"
                  data-testid="ceeb-accept-blocked"
                  disabled={!!acceptJob.running[b.id]}
                  style={{ ...actionBtnStyle, color: 'var(--status-deny)', borderColor: 'var(--status-deny-bar)' }}
                  title={`${lokaleLabel(b.apt_pending_ceeb)} z danymi z terenu nie weszło do CEEB — sprawdź przed akceptacją`}
                  onClick={() => setAcceptTarget({ buildingId: b.id, address: b.address })}
                >
                  {acceptJob.running[b.id] ? 'Akceptowanie…' : 'Akceptuj w CEEB ⚠'}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  icon="check"
                  disabled={!!acceptJob.running[b.id]}
                  style={{ ...actionBtnStyle, background: 'var(--status-ok, #2e7d32)', borderColor: 'var(--status-ok, #2e7d32)' }}
                  title="Zaakceptuje wystawiony protokół w CEEB (najpierw lokale, potem protokół) — NIEODWRACALNE"
                  onClick={() => setAcceptTarget({ buildingId: b.id, address: b.address })}
                >
                  {acceptJob.running[b.id] ? 'Akceptowanie…' : 'Akceptuj w CEEB'}
                </Button>
              )
            )}
            {/* [Zaplanuj I/II termin] — gdy zostały otwarte lokale (do powrotu), a przegląd nie ma
                jeszcze I lub II terminu. Numer z backendu (nextTermNumber = pierwszy WOLNY slot).
                Właściciel 2026-10-02 (docx): „Zaplanuj III termin — niepotrzebny (to ewentualność),
                można to zrobić odznaczając w okienku przed adresem" → III+ termin nie ma już
                przycisku w wierszu; przeplanowanie idzie przez zaznaczenie + „Zaplanuj dla
                zaznaczonych". Brak pola (stary backend) → bez przycisku, front nie zgaduje numeru. */}
            {b.apt_open > 0 && b.nextTermNumber != null && b.nextTermNumber <= 2 && !isQueued(b) && (
              <Button variant="secondary" icon="calendar" style={actionBtnStyle} onClick={() => openSecondTerm(b)}>
                {termButtonLabel(b)}
              </Button>
            )}
            {/* [Popraw wystawiony protokół w CEEB] (właściciel 2026-10-09) — superadmin + przełącznik CEEB_EDIT_ISSUED,
                protokół „Wprowadzony" z bieżącego roku, niezaakceptowany, lokale z terenu poza CEEB. Modal pyta serwer
                o bramkę; bot robi kopię przed zmianą. Celowo NIE w pasku akcji zbiorczych — tylko pojedynczo. */}
            {canEditIssuedRole && canEditIssued(b) && (
              <Button
                variant="secondary"
                icon="pencil"
                data-testid={`ceeb-edit-issued-${b.id}`}
                disabled={pushing}
                style={pushing ? actionBtnStyle : { ...actionBtnStyle, color: 'var(--status-rec)', borderColor: 'var(--status-rec-bar)' }}
                onClick={() => setEditIssuedFor(b)}
              >
                {pushing && ceebPush[b.id]?.mode === 'edit-issued' ? 'Poprawianie…' : 'Popraw wystawiony protokół w CEEB'}
              </Button>
            )}
            {/* Zaakceptowany + lokale z terenu poza CEEB: poprawka tylko ręcznie (anulowanie akceptacji — jeszcze nie w aplikacji). */}
            {canEditIssuedRole && acceptedNeedsManualFix(b) && (
              <span title={ACCEPTED_MANUAL_FIX_HINT} data-testid={`ceeb-edit-issued-accepted-${b.id}`} style={{ display: 'inline-flex' }}>
                <Button variant="secondary" icon="lock" disabled style={{ ...actionBtnStyle, pointerEvents: 'none' }}>
                  Popraw wystawiony protokół w CEEB
                </Button>
              </span>
            )}
            {/* [Zaplanuj] — NOWY przegląd tylko dla budynków bez aktywnego przeglądu
                (właściciel 2026-07-09): NOWY lub ZAMKNIETY (patrz PLANNABLE_STATES). Dla
                budynków z aktywną inspekcją POST i tak dałby 409 — mylący przycisk znika,
                a nawigację do istniejącego przeglądu pokrywa [Otwórz]. */}
            {(PLANNABLE_STATES.includes(b.lifecycle_state) || isQueued(b)) && (
              <Button variant="secondary" icon="calendar" style={actionBtnStyle} data-testid={`plan-${b.id}`} onClick={() => openPlan(b)}>
                Zaplanuj
              </Button>
            )}
            {/* Migr. 073 (właściciel 21.10): [Do kolejki] — bez terminów, od razu na tablety; [Usuń z kolejki] — wraca do „Nowy”. */}
            {canQueue(b) && (
              <Button variant="ghost" style={actionBtnStyle} data-testid={`queue-${b.id}`} onClick={() => setQueueTargets({ mode: 'add', targets: [b] })}>
                Do kolejki
              </Button>
            )}
            {isQueued(b) && (
              <Button variant="ghost" style={actionBtnStyle} data-testid={`unqueue-${b.id}`} onClick={() => setQueueTargets({ mode: 'remove', targets: [b] })}>
                Usuń z kolejki
              </Button>
            )}
            {/* [Odplanuj] (pkt 24) — ZAPLANOWANY i W_TERENIE: auto-release (decyzja 2026-08-03)
                robi z każdego przeglądu z terminem IN_PROGRESS = „W terenie", więc zaplanowany
                budynek praktycznie nigdy nie stoi w ZAPLANOWANY. Czy w terenie już coś zapisano,
                rozstrzyga backend (409 INSPECTION_HAS_VISITS) — tu nie zgadujemy. */}
            {(b.lifecycle_state === 'ZAPLANOWANY' || b.lifecycle_state === 'W_TERENIE') && b.inspectionId && !isQueued(b) && (
              <Button variant="ghost" style={actionBtnStyle} onClick={() => openUnplan(b)}>
                Odplanuj
              </Button>
            )}
            {/* [Otwórz] — zawsze. */}
            <Button variant="ghost" style={actionBtnStyle} onClick={() => handleOpen(b)}>
              Otwórz
            </Button>
          </Box>
        );
      },
    },
  ];

  return (
    <div className="tab-container">
      {/* Nagłówek + Dodaj budynek */}
      <div
        className="tab-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 'var(--space-4)',
        }}
      >
        <h2>Budynki</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
          {/* A11: import harmonogramu z Excela (podgląd → zatwierdzenie) — admin/superadmin (serwer: requireAdmin). */}
          {canImportSchedule && (
            <Button
              variant="secondary"
              icon="upload"
              data-testid="schedule-import-open"
              onClick={() => setImportOpen(true)}
              title="Wczytaj plik harmonogramu (Adres, Nr bud, Ilość mieszkań, Data I kontroli, Data popołudniu) — najpierw podgląd, potem zaplanowanie"
            >
              Importuj harmonogram (Excel)
            </Button>
          )}
          <Button variant="primary" icon="plus" onClick={() => setAddOpen(true)}>
            Dodaj budynek
          </Button>
        </div>
      </div>

      <div className="tab-content" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <CeebNightlyBanner />
        <CeebPushReportBanner />
        {/* CHIPY — filtr po stanie + licznik (spec §3). Domyślnie „Aktywne". */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', alignItems: 'center' }}>
          <ChipButton
            testId="pipeline-chip-active"
            label="Aktywne"
            count={activeCount}
            active={stage === null && !allStages}
            onClick={() => selectStage(null)}
          />
          {STATE_ORDER.map((s) => (
            <ChipButton
              key={s}
              testId={`pipeline-chip-${s}`}
              label={LIFECYCLE_META[s].label}
              count={stageCount(chipCounts, s)}
              active={stage === s}
              tintFg={LIFECYCLE_META[s].fg}
              onClick={() => selectStage(stage === s ? null : s)}
            />
          ))}
          {/* feat/bulk-reschedule-unplan (Bronek 08.09): zaległe jednym kliknięciem — do zmiany terminu albo odplanowania. */}
          <ChipButton
            testId="pipeline-chip-overdue"
            label="Po terminie"
            count={overdueCount}
            active={overdueOnly}
            tintFg="var(--status-deny)"
            onClick={selectOverdue}
          />
          {/* Migr. 073 (właściciel 21.10, „Zaplanuj do kolejki”): pula budynków bez terminów — już na tabletach. */}
          <ChipButton
            testId="pipeline-chip-queued"
            label="W kolejce"
            count={queuedCount}
            active={queuedOnly}
            onClick={selectQueued}
          />
          {/* 074 (właściciel 2026-10-21): protokół zniknął z CEEB — tych nie da się przywrócić, „Wyślij do CEEB” założy nowy. */}
          <ChipButton
            testId="pipeline-chip-ceeb-deleted"
            label="Usunięte w CEEB"
            count={ceebDeletedCount}
            active={ceebDeletedOnly}
            tintFg="var(--status-deny)"
            onClick={selectCeebDeleted}
          />
          {/* B1 (Bronek 21.10): „osobna kategoria dla budynków jednorodzinnych” — type=jednolokalowy, ze wszystkich etapów. */}
          <ChipButton
            testId="pipeline-chip-single-family"
            label="Jednorodzinne"
            count={singleFamilyCount}
            active={singleFamilyOnly}
            onClick={selectSingleFamily}
          />
        </div>

        {/* Szukajka + sort */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-4)', alignItems: 'flex-end' }}>
          <InputField
            label="Szukaj"
            placeholder="Szukaj (adres / CEEB / nr)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            containerStyle={{ maxWidth: 360, flex: '1 1 260px' }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <label
              htmlFor="pipeline-sort"
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-label)',
                fontWeight: 'var(--weight-semibold)',
                color: 'var(--text-secondary)',
              }}
            >
              Sortuj
            </label>
            <select
              id="pipeline-sort"
              value={sort}
              onChange={(e) => changeSort(e.target.value as PipelineSort)}
              style={{
                minHeight: 'var(--touch-min)',
                padding: '0 var(--space-4)',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-body)',
                color: 'var(--text-primary)',
                background: 'var(--surface-card)',
                border: 'var(--border-width-strong) solid var(--border-strong)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
              }}
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <Button
              variant="ghost"
              data-testid="pipeline-sort-dir"
              aria-label={sortDir === 'asc' ? 'Kierunek sortowania: rosnąco — kliknij, by odwrócić' : 'Kierunek sortowania: malejąco — kliknij, by odwrócić'}
              title="Odwróć kierunek sortowania (to samo, co drugie kliknięcie w nagłówek kolumny)"
              onClick={() => { setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); setPage(0); }}
              style={{ minHeight: 'var(--touch-min)', padding: '0 var(--space-3)' }}
            >
              {sortDir === 'asc' ? 'Rosnąco ↑' : 'Malejąco ↓'}
            </Button>
          </div>
          {/* E2: filtr zarządcy. Nazewnictwo „Zarządca" po decyzji właściciela 2026-08-04 (A7), nie „Klient".
              B3 (Bronek 21.10): pole z podpowiedziami po literach (nazwa / skrót) zamiast długiej listy — ds/ComboBox. */}
          <ComboBox
            id="pipeline-client"
            data-testid="pipeline-client-filter"
            label="Zarządca"
            emptyLabel="— wszyscy —"
            placeholder="— wszyscy — (wpisz nazwę lub skrót)"
            value={clientId}
            options={clientComboOptions(clientsInCategory)}
            onChange={(v) => { setClientId(v); setPage(0); }}
            containerStyle={{ width: 260 }}
          />
          {clientCategories.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <label
                htmlFor="pipeline-client-category"
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-label)',
                  fontWeight: 'var(--weight-semibold)',
                  color: 'var(--text-secondary)',
                }}
              >
                Kategoria zarządcy
              </label>
              <select
                id="pipeline-client-category"
                value={clientCategoryId}
                onChange={(e) => {
                  const next = e.target.value;
                  setClientCategoryId(next);
                  // wybrany zarządca spoza nowej kategorii → zdejmij go (inaczej lista byłaby pusta „bez powodu")
                  const cur = clients.find((c) => c.id === clientId);
                  if (cur && next && (next === 'none' ? !!cur.categoryId : cur.categoryId !== next)) setClientId('');
                  setPage(0);
                }}
                style={{
                  minHeight: 'var(--touch-min)',
                  maxWidth: 220,
                  padding: '0 var(--space-4)',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-body)',
                  color: 'var(--text-primary)',
                  background: 'var(--surface-card)',
                  border: 'var(--border-width-strong) solid var(--border-strong)',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                }}
              >
                <option value="">— wszystkie —</option>
                <option value="none">— bez kategorii —</option>
                {clientCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
          )}
          <Button
            variant="secondary"
            icon="download"
            onClick={openPack}
            disabled={zipBusy}
            style={{ minHeight: 'var(--touch-min)', padding: '0 var(--space-4)' }}
          >
            {zipBusy ? 'Pakowanie…' : 'Paczka wg filtrów'}
          </Button>
          {pendingCeeb > 0 && (
            <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: 'var(--space-2)' }}>
              {budynkiLabel(pendingCeeb)} z lokalami czekającymi na CEEB
            </span>
          )}
        </div>

        {/* Tryb „wszystkie etapy" (po kliknięciu „Pokaż wszystkie" w pustym stanie).
            Baner jest konieczny, bo żaden chip nie jest wtedy podświetlony — bez niego
            nie widać, czemu lista pokazuje budynki spoza „Aktywnych". */}
        {allStages && (
          <div
            data-testid="pipeline-all-stages-banner"
            style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap',
              padding: 'var(--space-3) var(--space-4)',
              background: 'var(--surface-fill)',
              border: 'var(--border-width) solid var(--border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-caption)',
              color: 'var(--text-secondary)',
            }}
          >
            <Icon name="building" size={18} color="var(--text-muted)" />
            <span>Szukasz we wszystkich etapach — filtr etapu jest wyłączony.</span>
            <Button variant="ghost" onClick={() => selectStage(null)} style={{ minHeight: 'var(--touch-min)' }}>
              Wróć do „Aktywnych"
            </Button>
          </div>
        )}

        {/* Podsumowanie wyników — orientacja przy dużej liście (3771+ budynków, #11). */}
        {!loadError && (
          <div
            data-testid="pipeline-summary"
            style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}
          >
            {loading ? 'Ładowanie…' : `Znaleziono ${budynkiLabel(total)}`}
          </div>
        )}

        {/* A12: wynik pojedynczego planowania (utworzone lokale / zarządca) — okno po sukcesie się zamyka. */}
        {planNotice && (
          <Box
            role={planNotice.kind === 'error' ? 'alert' : 'status'}
            data-testid="plan-notice"
            sx={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
              padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-md)',
              fontFamily: 'var(--font-sans)', fontSize: 'var(--text-label)',
              background: planNotice.kind === 'error' ? 'var(--status-deny-bg)' : 'var(--status-ok-bg)',
              border: `var(--border-width) solid ${planNotice.kind === 'error' ? 'var(--status-deny-bar)' : 'var(--status-ok-bar)'}`,
              color: planNotice.kind === 'error' ? 'var(--status-deny)' : 'var(--status-ok)',
            }}
          >
            <Icon name={planNotice.kind === 'error' ? 'triangle-alert' : 'check-circle'} size={18} />
            <span style={{ flex: 1 }}>{planNotice.text}</span>
            <Button variant="ghost" aria-label="Zamknij komunikat" onClick={() => setPlanNotice(null)}>Zamknij</Button>
          </Box>
        )}

        {/* Pasek akcji zbiorczej — pojawia się po zaznaczeniu budynków (multi-select). */}
        {selectedCount > 0 && (
          <Box
            data-testid="pipeline-bulk-bar"
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 'var(--space-3)',
              padding: 'var(--space-3) var(--space-4)',
              background: 'var(--changed-bg)',
              border: 'var(--border-width) solid var(--blue-700)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <span style={{ fontWeight: 'var(--weight-semibold)', color: 'var(--text-primary)' }}>
              Zaznaczono {budynkiLabel(selectedCount)}
            </span>
            <Box sx={{ flex: 1 }} />
            <Button variant="primary" icon="calendar" onClick={openBulkPlan} title="Ustaw ten sam termin przeglądu wszystkim zaznaczonym budynkom (także przeplanowanie)">
              Zaplanuj dla zaznaczonych
            </Button>
            {queueable().length > 0 && (
              <Button
                variant="secondary"
                data-testid="bulk-queue"
                onClick={() => setQueueTargets({ mode: 'add', targets: queueable() })}
                title="Dodaje budynki do kolejki bez terminów — od razu na tablety; I termin ustawi się sam przy pierwszym zapisanym lokalu"
              >
                Do kolejki ({queueable().length})
              </Button>
            )}
            {unqueueable().length > 0 && (
              <Button variant="secondary" data-testid="bulk-unqueue" onClick={() => setQueueTargets({ mode: 'remove', targets: unqueueable() })}>
                Usuń z kolejki ({unqueueable().length})
              </Button>
            )}
            {unplannable().length > 0 && (
              <Button variant="secondary" onClick={() => setBulkUnplanOpen(true)} title="Usuwa zaplanowany przegląd budynków, w których nic jeszcze nie wpisano — wracają do „Nowy”">
                Odplanuj zaznaczone ({unplannable().length})
              </Button>
            )}
            {/* Zapis zbiorczy — TYLKO wersje robocze i sekwencyjnie (jedna sesja CEEB).
                Filtrujemy do budynków, które faktycznie mają co wysłać: `apt_pending_ceeb > 0`
                (ten sam warunek, co przy pojedynczym przycisku w kolumnie „Akcje"). */}
            <Button
              variant="primary"
              icon="send"
              data-testid="bulk-ceeb-push"
              disabled={batchJob.status === 'running' || doWyslaniaIds().length === 0}
              onClick={() => setBatchConfirm(true)}
              title={doWyslaniaIds().length === 0
                ? 'Żaden z zaznaczonych budynków nie ma lokali do wpisania w wersję roboczą CEEB'
                : 'Zapisze w CEEB WERSJE ROBOCZE po kolei — protokołów NIE wystawia i NIE akceptuje. '
                  + 'Wystawienie: niebieski „Wyślij do CEEB” / „Finalizuj w CEEB” przy budynku; akceptacja: zielony „Akceptuj w CEEB”.'}
            >
              {/* Lista Bronka 12.10: „Wyślij do CEEB (N)" na pasku wyglądało jak wysyłka do końca
                  (Sikorskiego 11.10 — same wersje robocze, lokale potwierdzane ręcznie w CEEB). */}
              {batchJob.status === 'running'
                ? `Zapisywanie… (${batchJob.result?.done ?? 0}/${batchJob.result?.total ?? 0})`
                : `Zapisz wersje robocze (${doWyslaniaIds().length})`}
            </Button>
            {/* Akceptacja zbiorcza (2026-10-09) — tylko superadmin i tylko gdy w zaznaczeniu jest protokół
                „Wprowadzony". Licznik = budynki, które przejdą bramkę (wszystkie lokale z terenu w CEEB). */}
            {isSuperadmin && czekajaNaAkceptacjeIds().length > 0 && (
              <Button
                variant="primary"
                icon="check"
                data-testid="bulk-ceeb-accept"
                disabled={acceptBatch.status === 'running' || doAkceptacjiIds().length === 0}
                onClick={() => setAcceptBatchConfirm(true)}
                style={doAkceptacjiIds().length > 0 && acceptBatch.status !== 'running'
                  ? { background: 'var(--status-ok, #2e7d32)', borderColor: 'var(--status-ok, #2e7d32)' }
                  : undefined}
                title={doAkceptacjiIds().length === 0
                  ? 'Zaznaczone protokoły mają lokale z terenu poza CEEB — akceptuj pojedynczo, po sprawdzeniu w CEEB'
                  : 'Zaakceptuje protokoły „Wprowadzony" po kolei — NIEODWRACALNIE'}
              >
                {acceptBatch.status === 'running'
                  ? `Akceptowanie… (${acceptBatch.result?.done ?? 0}/${acceptBatch.result?.total ?? 0})`
                  : `Akceptuj w CEEB (${doAkceptacjiIds().length})`}
              </Button>
            )}
            {/* „Pobierz ▾" (właściciel 2026-10-02, docx: „Przyciski na górze – do wytłumaczenia"): cztery
                akcje pobierania zebrane pod jednym przyciskiem — pasek przestaje być ścianą przycisków.
                Panel zostaje otwarty po kliknięciu pozycji (widać „Pakowanie…"/postęp, rok protokołów
                wybiera się obok); zamyka go ponowny klik, Esc albo klik poza panelem. */}
            <div ref={downloadMenuRef} style={{ position: 'relative' }}>
              <Button
                variant="secondary"
                icon="download"
                data-testid="bulk-download-menu"
                aria-haspopup="true"
                aria-expanded={downloadMenuOpen}
                onClick={() => setDownloadMenuOpen((v) => !v)}
              >
                {zipBusy ? 'Pakowanie…' : xlsxBusy ? 'Generowanie…' : pdfJob.status === 'running'
                  ? `Pobieranie z CEEB… (${pdfJob.result?.fetched ?? 0}/${pdfJob.result?.total ?? '?'})`
                  : 'Pobierz ▾'}
              </Button>
              {downloadMenuOpen && (
                <div
                  role="group"
                  aria-label="Pobieranie dla zaznaczonych"
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + var(--space-1))',
                    left: 0,
                    zIndex: 20,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: 'var(--space-2)',
                    minWidth: 320,
                    padding: 'var(--space-3)',
                    background: 'var(--surface-card)',
                    border: 'var(--border-width) solid var(--border-strong)',
                    borderRadius: 'var(--radius-md)',
                    boxShadow: 'var(--shadow-float)',
                  }}
                >
                  {/* #416 (lista Bronka 12.10): KOMPLET do wysyłki zarządcy jednym kliknięciem — katalog na
                      budynek: protokoły CEEB (rok obok), podsumowanie kontroli, karty lokali z podpisami. */}
                  <Button
                    variant="primary"
                    icon="download"
                    data-testid="bulk-download-complete"
                    onClick={() => downloadProtocolPdfs(true)}
                    disabled={pdfJob.status === 'running' || selectedCount === 0}
                    title={`Jeden ZIP, katalog na budynek: protokoły z CEEB${pdfYear ? ` z roku ${pdfYear}` : ' (wszystkie lata)'}, podsumowanie kontroli i karty lokali z podpisami lokatorów`}
                    style={{ justifyContent: 'flex-start' }}
                  >
                    {pdfJob.status === 'running' ? 'Przygotowywanie paczki…' : `Pobierz komplet: protokoły + podsumowania + karty (${selectedCount})`}
                  </Button>
                  <Button
                    variant="ghost"
                    icon="download"
                    onClick={downloadSummariesZip}
                    disabled={zipBusy || selectedCount === 0}
                    title={'Podsumowania „Nieprawidłowości” zaznaczonych budynków — jeden ZIP, osobny plik per adres'}
                    style={{ justifyContent: 'flex-start' }}
                  >
                    {zipBusy ? 'Pakowanie…' : `Pobierz podsumowania (${selectedCount})`}
                  </Button>
                  <label
                    style={{
                      display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
                      padding: '0 var(--space-4)', fontFamily: 'var(--font-sans)',
                      fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', cursor: 'pointer',
                    }}
                    title="Dotyczy „Pobierz podsumowania”: dodatkowa strona z wykazem lokali, ich statusem końcowym i przebiegiem kontroli"
                  >
                    <input
                      type="checkbox"
                      checked={zipAppendix}
                      disabled={zipBusy}
                      onChange={(e) => setZipAppendix(e.target.checked)}
                    />
                    Dołącz stronę: status i przebieg kontroli w lokalach
                  </label>
                  {/* Bronek 14.10: dotyczy „Pobierz komplet” i „Pobierz podsumowania” — wspólna opcja panelu. */}
                  <SummaryHeatSourcesOption disabled={zipBusy} style={{ display: 'flex', padding: '0 var(--space-4)' }} />
                  <Button
                    variant="ghost"
                    icon="download"
                    onClick={downloadScheduleXlsx}
                    disabled={xlsxBusy || selectedCount === 0}
                    title="Grafik terminów zaznaczonych budynków jako plik Excela (jeden wiersz = jeden budynek, I i II termin w osobnych kolumnach)"
                    style={{ justifyContent: 'flex-start' }}
                  >
                    {xlsxBusy ? 'Generowanie…' : `Pobierz grafik (${selectedCount})`}
                  </Button>
                  <Button
                    variant="ghost"
                    icon="mail"
                    data-testid="bulk-schedule-mail"
                    onClick={openScheduleMail}
                    disabled={selectedCount === 0}
                    title="Ten sam grafik Excela wysłany z serwera e-mailem — domyślnie do zarządcy, gdy wszystkie zaznaczone budynki mają tego samego"
                    style={{ justifyContent: 'flex-start' }}
                  >
                    {`Wyślij grafik e-mailem (${selectedCount})`}
                  </Button>
                  <Button
                    variant="ghost"
                    icon="clipboard"
                    data-testid="bulk-case-numbers"
                    onClick={() => { setCaseNumbersIds(selectedBuildingIds()); setDownloadMenuOpen(false); }}
                    disabled={selectedCount === 0}
                    title="Numery spraw CEEB zaznaczonych budynków (bieżący rok albo ostatni znany) — lista do skopiowania lub CSV"
                    style={{ justifyContent: 'flex-start' }}
                  >
                    {`Numery protokołów (${selectedCount})`}
                  </Button>
                  <Button
                    variant="ghost"
                    icon="flame"
                    data-testid="bulk-heat-source-summary"
                    onClick={() => { setHsSummaryIds(selectedBuildingIds()); setDownloadMenuOpen(false); }}
                    title="Ile indywidualnych źródeł ciepła każdego typu jest w zaznaczonych budynkach — wiersz per budynek i suma"
                    style={{ justifyContent: 'flex-start' }}
                  >
                    {`Podsumowanie źródeł ciepła (${selectedCount})`}
                  </Button>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', borderTop: 'var(--border-width) solid var(--border)', paddingTop: 'var(--space-2)' }}>
                    {/* E5 (§8): OFICJALNE PDF-y protokołów z CEEB (jasper) — job w tle + ZIP. */}
                    <Button
                      variant="ghost"
                      icon="download"
                      onClick={() => downloadProtocolPdfs()}
                      disabled={pdfJob.status === 'running' || selectedCount === 0}
                      title={pdfYear
                        ? `Oficjalne PDF-y protokołów z CEEB dla zaznaczonych budynków — rok ${pdfYear}`
                        : 'Oficjalne PDF-y protokołów z CEEB dla zaznaczonych budynków (wszystkie lata z historii)'}
                      style={{ justifyContent: 'flex-start', flex: 1 }}
                    >
                      {pdfJob.status === 'running'
                        ? `Pobieranie z CEEB… (${pdfJob.result?.fetched ?? 0}/${pdfJob.result?.total ?? '?'})`
                        : `Pobierz protokoły CEEB (${selectedCount})`}
                    </Button>
                    {/* E6: rocznik dla protokołów CEEB — domyślnie bieżący rok (właściciel 2026-10-02),
                        „wszystkie lata" nadal do wyboru. Dotyczy też kompletu (#416) — protokoły i karty z tego roku. */}
                    <ProtocolYearSelect value={pdfYear} onChange={setPdfYear} disabled={pdfJob.status === 'running'} />
                  </div>
                </div>
              )}
            </div>
            <Button
              variant="secondary"
              icon="circle-dot"
              data-testid="bulk-unchecked-apartments"
              onClick={() => setUncheckedIds(selectedBuildingIds())}
              title="Lokale bez wyniku zamykającego (nieobecność, odmowa, nieodwiedzone) w zaznaczonych budynkach — lista, CSV, kopiowanie"
            >
              {`Niesprawdzone lokale (${selectedCount})`}
            </Button>
            <Button
              variant="secondary"
              icon="users"
              data-testid="bulk-assign-client"
              onClick={openAssign}
              title="Przypisz zaznaczone budynki do zarządcy (zmiana obecnego zarządcy wymaga potwierdzenia)"
            >
              {`Przypisz zarządcę (${selectedCount})`}
            </Button>
            <Button
              variant="ghost"
              icon="help"
              data-testid="bulk-help"
              aria-label="Co robią te przyciski"
              title="Co robią te przyciski"
              onClick={() => setBulkHelpOpen(true)}
            >
              ?
            </Button>
            <Button variant="ghost" onClick={clearSelection} title="Odznacza wszystkie budynki (nic nie zmienia w danych)">
              Wyczyść zaznaczenie
            </Button>
            {/* Lista Bronka 12.10: widoczna podpowiedź, nie tylko dymek — pasek zbiorczy zapisuje WYŁĄCZNIE
                wersje robocze (decyzja właściciela, bezpieczeństwo); wystawienie i akceptacja są przy budynku. */}
            <Box
              data-testid="bulk-ceeb-hint"
              sx={{ flexBasis: '100%', fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}
            >
              Pasek zapisuje w CEEB tylko <strong>wersje robocze</strong>. Protokół wystawia niebieski
              {' „Wyślij do CEEB”'} (albo {'„Finalizuj w CEEB”'}) w wierszu budynku, akceptuje zielony {'„Akceptuj w CEEB”'}.
            </Box>
            {zipMsg && (
              <Box
                role="alert"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  width: '100%',
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-label)',
                  background: zipMsg.kind === 'error' ? 'var(--status-deny-bg)' : 'var(--status-rec-bg)',
                  border: `var(--border-width) solid ${zipMsg.kind === 'error' ? 'var(--status-deny-bar)' : 'var(--status-rec-bar)'}`,
                  color: zipMsg.kind === 'error' ? 'var(--status-deny)' : 'var(--status-rec)',
                }}
              >
                <Icon name="triangle-alert" size={18} />
                <span>{zipMsg.text}</span>
              </Box>
            )}
          </Box>
        )}

        {/* Lista server-side (paginacja server-side; sort przez dropdown → API). */}
        {loadError ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              p: 'var(--space-5)',
              border: 'var(--border-width) solid var(--status-deny-bar)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--status-deny-bg)',
              color: 'var(--status-deny)',
            }}
          >
            <Icon name="triangle-alert" size={24} />
            <span style={{ flex: 1, fontWeight: 'var(--weight-semibold)' }}>{loadError}</span>
            <Button variant="secondary" icon="refresh" onClick={loadRows}>Spróbuj ponownie</Button>
          </Box>
        ) : (
          <Box sx={{ maxWidth: '100%', overflowX: 'auto' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(r) => (r as PipelineBuilding).id}
              autoHeight
              getRowHeight={() => 'auto'}
              checkboxSelection
              keepNonExistentRowsSelected
              rowSelectionModel={selection}
              onRowSelectionModelChange={(m) => setSelection(m)}
              disableRowSelectionOnClick
              disableColumnMenu
              disableVirtualization
              paginationMode="server"
              sortingMode="server"
              sortModel={SORT_TO_FIELD[sort] ? [{ field: SORT_TO_FIELD[sort] as string, sort: sortDir }] : []}
              sortingOrder={['asc', 'desc']}
              onSortModelChange={(m) => {
                const next = m[0]?.field ? FIELD_TO_SORT[m[0].field] : undefined;
                if (!next) return;
                if (next !== sort) { changeSort(next); return; }
                // ten sam nagłówek → odwrócenie kierunku (MUI podaje nowy kierunek; bez niego przełączamy sami)
                const dir = (m[0].sort === 'asc' || m[0].sort === 'desc') ? m[0].sort : (sortDir === 'asc' ? 'desc' : 'asc');
                if (dir !== sortDir) { setSortDir(dir); setPage(0); }
              }}
              rowCount={total}
              paginationModel={{ page, pageSize }}
              onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize); }}
              pageSizeOptions={[25, 50, 100]}
              loading={loading}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              localeText={plPL.components.MuiDataGrid.defaultProps.localeText}
              sx={{ ...dataGridSx, width: '100%' }}
            />
          </Box>
        )}
      </div>

      {/* [Otwórz] — podgląd budynku/przeglądu (reużyty komponent). */}
      {openBuilding && (
        <BuildingInspectionView
          buildingId={openBuilding.buildingId}
          inspectionId={openBuilding.inspectionId}
          flueCountsMissing={openBuilding.row?.flue_counts_missing === true}
          onClose={() => { setOpenBuilding(null); refresh(); }}
          onPlanInspection={
            openBuilding.row && !openBuilding.inspectionId
              ? () => {
                  const row = openBuilding.row!;
                  setOpenBuilding(null);
                  openPlan(row);
                }
              : undefined
          }
        />
      )}

      {/* Raport bota CEEB (#51b) — pojawia się od razu po zakończeniu [Wyślij do CEEB],
          gdy last-meta.json (fillCeebDraft.js) potwierdza dopasowanie do wysłanego
          przeglądu. Ten sam raport widać też trwale w BuildingInspectionView (sekcja
          CEEB) po [Otwórz], więc zamknięcie tego modala niczego nie „gubi". */}
      {/* Finalizacja gotowego draftu (2026-09-15) — nieodwracalne wystawienie protokołu. */}
      <ConfirmModal
        open={!!finalizeConfirmFor}
        title={finalizeConfirmFor ? `Finalizować protokół w CEEB dla „${finalizeConfirmFor.address}”?` : ''}
        message={(
          <div>
            {drugiProtokolDoFinalizacji(finalizeConfirmFor) && (
              // #404 (D6): drugi protokół w roku — bez tego okno wyglądało jak zwykła finalizacja i duplikat szedł jednym kliknięciem.
              <div role="alert" data-testid="finalize-second-protocol" style={{
                margin: '0 0 var(--space-3)', padding: 'var(--space-3)', borderRadius: 'var(--radius-sm)',
                background: 'var(--status-deny-bg)', borderLeft: '3px solid var(--status-deny-bar)',
              }}>
                <p style={{ margin: '0 0 var(--space-2)', fontWeight: 'var(--weight-semibold)', color: 'var(--status-deny)' }}>
                  W CEEB jest już wystawiony/zaakceptowany protokół z tego roku pod tym adresem
                  ({numeryDrugiegoProtokolu(finalizeConfirmFor)}). Wystaw tylko, jeśli to drugi termin / zaległa kontrola;
                  jeśli to duplikat — nie wystawiaj, zgłoś administratorowi.
                </p>
                <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-start', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    data-testid="finalize-second-protocol-ack"
                    checked={finalizeSecondAck}
                    onChange={(e) => setFinalizeSecondAck(e.target.checked)}
                  />
                  <span>Sprawdziłem w CEEB — to drugi termin / zaległa kontrola, nie duplikat.</span>
                </label>
              </div>
            )}
            <p style={{ margin: '0 0 var(--space-3)' }}>
              Numer sprawy: <strong>{finalizeConfirmFor?.ceebCaseNumber || '—'}</strong>.
              Bot <strong>nie zmieni treści</strong> protokołu — tylko wyśle inwentaryzacje lokali i budynku
              („Wyślij”) i zapisze protokół („Zapisz”), czyli status w CEEB: Wersja robocza → Wprowadzony.
            </p>
            <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>
              Wystawienie protokołu jest nieodwracalne. Po potwierdzeniu w rejestrze pojawi się zielony „Akceptuj w CEEB”.
            </p>
          </div>
        )}
        confirmLabel="Finalizuj w CEEB"
        cancelLabel="Anuluj"
        confirmDisabled={drugiProtokolDoFinalizacji(finalizeConfirmFor) && !finalizeSecondAck}
        onConfirm={() => {
          if (!finalizeConfirmFor || (drugiProtokolDoFinalizacji(finalizeConfirmFor) && !finalizeSecondAck)) return;
          handleFinalizeInCeeb(finalizeConfirmFor);
        }}
        onCancel={() => setFinalizeConfirmFor(null)}
      />
      {/* Wysyłka zbiorcza — potwierdzenie z listą adresów. Operator ma zobaczyć, CO wysyła,
          zanim ruszy sekwencja trwająca kilkanaście minut na budynek. */}
      <ConfirmModal
        open={batchConfirm}
        title={`Zapisać wersje robocze w CEEB — ${budynkiLabel(doWyslaniaIds().length)}?`}
        message={(
          <div>
            <p style={{ margin: '0 0 var(--space-3)' }}>
              Zapiszę <strong>wersje robocze</strong> — protokoły NIE zostaną wystawione.
              Budynki idą <strong>po kolei</strong> (bot pracuje na jednej sesji CEEB),
              więc partia potrwa kilkanaście minut na budynek.
            </p>
            <p style={{ margin: '0 0 var(--space-3)' }}>
              Żeby protokół został <strong>wystawiony</strong> („Wprowadzony”), użyj potem niebieskiego
              {' „Finalizuj w CEEB”'} w wierszu budynku (albo od razu {'„Wyślij do CEEB”'} przy budynku zamiast paska).
            </p>
            <ul style={{ margin: 0, paddingLeft: 'var(--space-4)', maxHeight: 200, overflowY: 'auto' }}>
              {doWyslaniaIds().map((id) => (
                <li key={id} style={{ fontSize: 'var(--text-caption)' }}>{addressOf(id)}</li>
              ))}
            </ul>
            {selectedCount > doWyslaniaIds().length && (
              <p style={{ margin: 'var(--space-3) 0 0', color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>
                Pomijam {budynkiLabel(selectedCount - doWyslaniaIds().length)} bez lokali do wpisania w wersję roboczą
                (albo z protokołem już wystawionym — te czekają na „Akceptuj w CEEB”).
              </p>
            )}
            {/* pkt 14: to samo ostrzeżenie, co przy pojedynczym „Wyślij do CEEB" — wysłać można. */}
            {flueMissingIds().length > 0 && (
              <div
                data-testid="batch-flue-warning"
                style={{
                  margin: 'var(--space-3) 0 0',
                  padding: 'var(--space-3)',
                  border: 'var(--border-width) solid var(--status-deny)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-primary)',
                  fontSize: 'var(--text-caption)',
                }}
              >
                <p style={{ margin: '0 0 var(--space-2)', display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontWeight: 'var(--weight-semibold)' }}>
                  <span style={{ display: 'inline-flex', color: 'var(--status-deny)' }}>
                    <Icon name="triangle-alert" size={16} />
                  </span>
                  Dane zakresu kontroli nie zostały uzupełnione
                </p>
                <p style={{ margin: '0 0 var(--space-2)' }}>
                  Liczby przewodów (dymowe, spalinowe, wentylacyjne, awaryjne) są w CEEB puste albo zerowe
                  w budynkach poniżej ({flueMissingIds().length}). Kominiarz uzupełnia je ręcznie w CEEB — wysyłka i tak może ruszyć.
                </p>
                <ul style={{ margin: 0, paddingLeft: 'var(--space-4)' }}>
                  {flueMissingIds().map((id) => (
                    <li key={id}>{addressOf(id)}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        confirmLabel={flueMissingIds().length > 0 ? 'Zapisz mimo to' : 'Zapisz wersje robocze w CEEB'}
        onConfirm={() => { setBatchConfirm(false); void batchJob.start(doWyslaniaIds()); }}
        onCancel={() => setBatchConfirm(false)}
      />

      {/* Wynik partii — triage jak w zbiorczym planowaniu: ✓ wysłane / ⚠ pominięte / ✕ błędy. */}
      <ModalShell
        open={batchJob.status === 'done' || batchJob.status === 'error'}
        title="Wynik wysyłki zbiorczej"
        onClose={() => { batchJob.reset(); clearSelection(); refresh(); }}
        footer={<Button variant="primary" onClick={() => { batchJob.reset(); clearSelection(); refresh(); }}>Zamknij</Button>}
      >
        {batchJob.result?.error && (
          <p style={{ color: 'var(--status-deny)' }}>{batchJob.result.error}</p>
        )}
        {(batchJob.result?.wyniki || []).map((w) => (
          <div key={w.buildingId} style={{ display: 'flex', gap: 'var(--space-2)', padding: 'var(--space-2) 0', borderBottom: '1px solid var(--border-subtle)' }}>
            <span aria-hidden style={{ width: '1.5em' }}>
              {w.status === 'ok' ? '✓' : w.status === 'pominiety' ? '⚠' : '✕'}
            </span>
            <span style={{ flex: 1 }}>
              <strong>{w.adres}</strong>
              {w.status === 'ok' && w.apartmentCount != null && (
                <span style={{ color: 'var(--text-muted)' }}> — lokale {w.synced}/{w.apartmentCount}</span>
              )}
              {w.error && <span style={{ color: 'var(--text-muted)' }}> — {w.error}</span>}
              {(w.ostrzezeniaDanych || []).length > 0 && (
                <span style={{ color: 'var(--changed-text)' }}> · ⚠ dane budynku ({w.ostrzezeniaDanych!.length})</span>
              )}
              {w.notice && (
                <span data-testid={`ceeb-batch-notice-${w.buildingId}`} style={{ display: 'block', color: 'var(--changed-text)' }}>⚠ {w.notice}</span>
              )}
            </span>
          </div>
        ))}
      </ModalShell>

      {/* Akceptacja zbiorcza — potwierdzenie z listą spraw i z listą POMINIĘTYCH (z powodem). */}
      <ConfirmModal
        open={acceptBatchConfirm}
        title={`Zaakceptować w CEEB ${budynkiLabel(doAkceptacjiIds().length)}?`}
        message={(
          <div data-testid="bulk-accept-confirm">
            <p style={{ margin: '0 0 var(--space-3)' }}>
              Akceptacja jest <strong>NIEODWRACALNA</strong> — status w CEEB zmieni się z „Wprowadzony” na
              „Zaakceptowany”. Budynki idą <strong>po kolei</strong> (jedna sesja CEEB).
            </p>
            <ul style={{ margin: 0, paddingLeft: 'var(--space-4)', maxHeight: 200, overflowY: 'auto' }}>
              {doAkceptacjiIds().map((id) => {
                const r = rows.find((x) => x.id === id);
                return (
                  <li key={id} style={{ fontSize: 'var(--text-caption)' }}>
                    {addressOf(id)}{r?.ceebCaseNumber ? ` — sprawa ${r.ceebCaseNumber}` : ''}
                  </li>
                );
              })}
            </ul>
            {czekajaNaAkceptacjeIds().length > doAkceptacjiIds().length && (
              <div data-testid="bulk-accept-skipped" style={{ margin: 'var(--space-3) 0 0', color: 'var(--text-primary)', fontSize: 'var(--text-caption)' }}>
                <p style={{ margin: '0 0 var(--space-1)', fontWeight: 'var(--weight-semibold)' }}>
                  Pomijam — dane z terenu nie weszły do CEEB (akceptacja tylko pojedynczo, po sprawdzeniu w CEEB):
                </p>
                <ul style={{ margin: 0, paddingLeft: 'var(--space-4)' }}>
                  {czekajaNaAkceptacjeIds().filter((id) => !doAkceptacjiIds().includes(id)).map((id) => (
                    <li key={id}>{addressOf(id)} — {lokaleLabel(rows.find((x) => x.id === id)?.apt_pending_ceeb ?? 0)}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        confirmLabel="Akceptuj w CEEB"
        onConfirm={() => { setAcceptBatchConfirm(false); void acceptBatch.start(doAkceptacjiIds()); }}
        onCancel={() => setAcceptBatchConfirm(false)}
      />

      <ModalShell
        open={acceptBatch.status === 'done' || acceptBatch.status === 'error'}
        title="Wynik akceptacji zbiorczej"
        onClose={() => { acceptBatch.reset(); clearSelection(); refresh(); }}
        footer={<Button variant="primary" onClick={() => { acceptBatch.reset(); clearSelection(); refresh(); }}>Zamknij</Button>}
      >
        {acceptBatch.result?.error && (
          <p style={{ color: 'var(--status-deny)' }}>{acceptBatch.result.error}</p>
        )}
        {(acceptBatch.result?.wyniki || []).map((w) => (
          <div key={w.buildingId} style={{ display: 'flex', gap: 'var(--space-2)', padding: 'var(--space-2) 0', borderBottom: '1px solid var(--border-subtle)' }}>
            <span aria-hidden style={{ width: '1.5em' }}>
              {w.status === 'ok' ? '✓' : w.status === 'pominiety' ? '⚠' : '✕'}
            </span>
            <span style={{ flex: 1 }}>
              <strong>{w.adres}</strong>
              {w.status === 'ok' && <span style={{ color: 'var(--text-muted)' }}> — zaakceptowany{w.caseNumber ? ` (${w.caseNumber})` : ''}</span>}
              {w.error && <span style={{ color: 'var(--text-muted)' }}> — {w.error}</span>}
            </span>
          </div>
        ))}
      </ModalShell>

      <CeebAcceptModal target={acceptTarget} onCancel={() => setAcceptTarget(null)} onConfirm={confirmAccept} />
      <ModalShell
        open={!!acceptOutcome}
        title={acceptOutcome?.status === 'done' ? 'Protokół zaakceptowany w CEEB' : 'Akceptacja nie powiodła się'}
        onClose={() => setAcceptOutcome(null)}
        footer={<Button variant="primary" onClick={() => setAcceptOutcome(null)}>Zamknij</Button>}
      >
        {acceptOutcome && (
          <div data-testid="ceeb-accept-outcome">
            <p style={{ margin: 0 }}><strong>{acceptOutcome.address}</strong></p>
            {acceptOutcome.status === 'done'
              ? <p style={{ margin: 'var(--space-2) 0 0' }}>{mailLabel(acceptOutcome.mail) || 'Status w CEEB: Zaakceptowany.'}</p>
              : <p role="alert" style={{ margin: 'var(--space-2) 0 0', color: 'var(--status-deny)' }}>{acceptOutcome.error}</p>}
          </div>
        )}
      </ModalShell>

      <ModalShell
        open={!!(ceebReportBuilding && ceebReportMeta)}
        onClose={() => setCeebReportBuilding(null)}
        width="min(90vw, 720px)"
        title={ceebReportBuilding ? ceebReportBuilding.address : undefined}
      >
        {ceebReportMeta && <CeebBotReport meta={ceebReportMeta} />}
      </ModalShell>

      {/* Modal [Zaplanuj] — nowy przegląd dla NOWY. */}
      <ModalShell
        open={planFor !== null}
        onClose={closePlan}
        width={520}
        title="Zaplanuj przegląd"
        footer={
          <>
            <Button variant="secondary" onClick={closePlan}>Anuluj</Button>
            <Button
              variant="primary"
              disabled={!planForm.firstTermDate || !!planDateErr || !!planAptErr || !!planClientError(planClient, clients) || planSaving}
              onClick={savePlan}
            >
              {planSaving ? 'Zapisywanie…' : 'Zaplanuj przegląd'}
            </Button>
          </>
        }
      >
        {planFor && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            {/* #52 (B8) — 409: budynek ma już aktywną inspekcję (warn, nie error-crash). */}
            {planConflict && (
              <Box
                role="alert"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-2)',
                  padding: 'var(--space-3) var(--space-4)',
                  background: 'var(--status-rec-bg)',
                  border: 'var(--border-width) solid var(--status-rec-bar)',
                  borderRadius: 'var(--radius-md)',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-label)',
                  color: 'var(--status-rec)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontWeight: 'var(--weight-semibold)' }}>
                  <Icon name="triangle-alert" size={18} />
                  <span>{planConflict.message}</span>
                </Box>
                {planConflict.existingInspectionId && (
                  <Button
                    variant="secondary"
                    icon="arrow-right"
                    onClick={goToExistingInspection}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    Przejdź do istniejącego
                  </Button>
                )}
              </Box>
            )}
            <Box sx={{ fontSize: 'var(--text-body)', color: 'var(--text-primary)' }}>{planFor.address}</Box>
            <PlanningCeebNote testId="plan-ceeb-note" />
            <InputField
              label="I termin"
              type="date"
              min={termBounds.min}
              max={termBounds.max}
              value={planForm.firstTermDate}
              errorMessage={termDateError(planForm.firstTermDate) || undefined}
              onChange={(e) => setPlanForm((f) => ({ ...f, firstTermDate: e.target.value }))}
            />
            <InputField
              label="II termin (opcjonalnie)"
              type="date"
              min={termBounds.min}
              max={termBounds.max}
              value={planForm.secondTermDate}
              errorMessage={termDateError(planForm.secondTermDate) || undefined}
              onChange={(e) => setPlanForm((f) => ({ ...f, secondTermDate: e.target.value }))}
            />
            <InputField
              label="Przypisany kominiarz (opcjonalnie)"
              value={planForm.assignedTo}
              onChange={(e) => setPlanForm((f) => ({ ...f, assignedTo: e.target.value }))}
            />
            {/* A12: zarządca (+ e-mail) i liczba lokali — opcjonalnie, w jednym kroku z planowaniem. */}
            <PlanClientFields
              idPrefix="plan"
              clients={clients}
              value={planClient}
              onChange={setPlanClient}
              currentClientId={planFor.clientId ?? null}
              currentClientName={planFor.clientShortName || planFor.clientName || null}
              disabled={planSaving}
            />
            {/* 078: z którego protokołu CEEB import bierze dane przeglądu (zakres, lokale, źródła). */}
            <PlanPrefillSourceField
              idPrefix="plan"
              buildingId={planFor.id}
              value={planSource}
              onChange={setPlanSource}
              disabled={planSaving}
            />
            {planFor.apt_total === 0 && !isQueued(planFor) ? (
              <InputField
                label="Liczba lokali (opcjonalnie)"
                inputMode="numeric"
                value={planForm.apartmentCount}
                disabled={planSaving}
                placeholder="np. 6"
                hint="Budynek nie ma jeszcze lokali — utworzymy lokale o numerach 1..N (jak „Dodaj lokale od–do”)."
                errorMessage={planAptErr || undefined}
                onChange={(e) => setPlanForm((f) => ({ ...f, apartmentCount: e.target.value }))}
                containerStyle={{ maxWidth: 260 }}
              />
            ) : (
              <Box data-testid="plan-apartments-existing" sx={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
                Budynek ma {lokaleLabel(planFor.apt_total)} — lokale dodasz w widoku budynku.
              </Box>
            )}
            {/* jw. (D8) — ten sam wyszarzony przycisk, ta sama brakująca informacja zwrotna. */}
            {!planForm.firstTermDate && (
              <Box data-testid="plan-hint" sx={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
                Podaj I termin, żeby zaplanować przegląd.
              </Box>
            )}
          </Box>
        )}
      </ModalShell>

      {/* Modal [Zaplanuj II termin] — additional-terms (slot 2) dla otwartych lokali (DO_POWROTU). */}
      <ModalShell
        open={termFor !== null}
        onClose={() => setTermFor(null)}
        width={520}
        title={termFor ? termButtonLabel(termFor) : 'Zaplanuj kolejny termin'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setTermFor(null)}>Anuluj</Button>
            <Button variant="primary" disabled={!termForm.date || !!termDateError(termForm.date) || termSaving} onClick={saveSecondTerm}>
              {termSaving ? 'Zapisywanie…' : 'Dodaj termin'}
            </Button>
          </>
        }
      >
        {termFor && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            <Box sx={{ fontSize: 'var(--text-body)', color: 'var(--text-primary)' }}>
              {termFor.address}
              {termFor.apt_open > 0 && (
                <Box sx={{ mt: 'var(--space-1)', fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
                  {lokaleLabel(termFor.apt_open)} do powrotu
                </Box>
              )}
            </Box>
            <PlanningCeebNote testId="term-plan-ceeb-note" />
            <InputField
              label="Data kolejnego terminu"
              type="date"
              min={termBounds.min}
              max={termBounds.max}
              value={termForm.date}
              errorMessage={termDateError(termForm.date) || undefined}
              onChange={(e) => setTermForm((f) => ({ ...f, date: e.target.value }))}
            />
            <InputField
              label="Powód (opcjonalnie)"
              value={termForm.reason}
              onChange={(e) => setTermForm((f) => ({ ...f, reason: e.target.value }))}
            />
          </Box>
        )}
      </ModalShell>

      {/* Modal [Zaplanuj dla zaznaczonych] — zbiorcze planowanie (multi-select). Reużywa
          endpoint POST /inspections per budynek (bez zmiany kontraktu backendu); 409 =
          budynek ma już aktywny przegląd → trafia do „pominięte", nie przerywa pętli. */}
      <ModalShell
        open={bulkOpen}
        onClose={closeBulkPlan}
        width={520}
        title={bulkResult ? 'Wynik zbiorczego planowania' : 'Zaplanuj dla zaznaczonych'}
        footer={
          bulkResult ? (
            <Button variant="primary" onClick={closeBulkPlan}>Zamknij</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={closeBulkPlan}>Anuluj</Button>
              <Button
                variant="primary"
                disabled={!bulkForm.firstTermDate || !!bulkDateErr || !!planClientError(bulkClient, clients) || bulkSaving || selectedCount === 0}
                onClick={runBulkPlan}
              >
                {bulkSaving ? 'Planowanie…' : `Zaplanuj (${selectedCount})`}
              </Button>
            </>
          )
        }
      >
        {bulkResult ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', fontSize: 'var(--text-body)' }}>
              <span style={{ color: 'var(--status-ok)', fontWeight: 'var(--weight-semibold)' }}>
                ✓ Zaplanowano: {bulkResult.planned.length}
              </span>
              {bulkResult.rescheduled.length > 0 && (
                <span style={{ color: 'var(--status-ok)' }}>
                  ✓ Zmieniono termin: {bulkResult.rescheduled.length}
                </span>
              )}
              {bulkResult.skipped.length > 0 && (
                <span style={{ color: 'var(--status-rec)' }}>
                  ⚠ Pominięto (aktywny przegląd, bez zmiany terminu): {bulkResult.skipped.length}
                </span>
              )}
              {bulkResult.errors.length > 0 && (
                <span style={{ color: 'var(--status-deny)' }}>✕ Błędy: {bulkResult.errors.length}</span>
              )}
              {bulkResult.client && (
                <span data-testid="bulk-plan-client-result" style={{ color: 'var(--status-ok)' }}>
                  ✓ {planClientSummary(bulkResult.client)}
                  {bulkResult.client.others.length > 0 ? ' Po zamknięciu zapytamy o zmianę zarządcy.' : ''}
                </span>
              )}
              {bulkResult.clientError && (
                <span role="alert" style={{ color: 'var(--status-deny)' }}>✕ Zarządca: {bulkResult.clientError}</span>
              )}
            </Box>
            {bulkResult.skipped.length > 0 && (
              <Box sx={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
                Pominięte: {bulkResult.skipped.join(', ')}
              </Box>
            )}
            {bulkResult.errors.length > 0 && (
              <Box sx={{ fontSize: 'var(--text-caption)', color: 'var(--status-deny)' }}>
                {bulkResult.errors.map((er, i) => (
                  <div key={i}>{er.address}: {er.message}</div>
                ))}
              </Box>
            )}
            {/* 078: rok bez protokołu w lustrze → dane z protokołu bieżącego (albo błąd zapisu źródła). */}
            {bulkResult.sourceNotes && bulkResult.sourceNotes.length > 0 && (
              <Box data-testid="bulk-plan-source-notes" sx={{ fontSize: 'var(--text-caption)', color: 'var(--status-rec)' }}>
                {bulkResult.sourceNotes.map((n, i) => (
                  <div key={i}>{n.address}: {n.message}</div>
                ))}
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            <Box sx={{ fontSize: 'var(--text-body)', color: 'var(--text-primary)' }}>
              Planujesz przegląd dla <strong>{budynkiLabel(selectedCount)}</strong>.
            </Box>
            <PlanningCeebNote testId="bulk-plan-ceeb-note" />
            {selectedWithOpenInspection().length > 0 && (
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)', cursor: 'pointer', fontSize: 'var(--text-label)' }}>
                <input type="checkbox" checked={bulkReschedule} disabled={bulkSaving} onChange={(e) => setBulkReschedule(e.target.checked)} />
                <span>
                  Zmień też termin w {budynkiLabel(selectedWithOpenInspection().length)} z aktywnym przeglądem
                  <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>
                    {selectedWithOpenInspection().map((r) => r.address).join(' · ')}
                  </span>
                </span>
              </label>
            )}
            <InputField
              label="I termin"
              type="date"
              min={termBounds.min}
              max={termBounds.max}
              value={bulkForm.firstTermDate}
              errorMessage={termDateError(bulkForm.firstTermDate) || undefined}
              onChange={(e) => setBulkForm((f) => ({ ...f, firstTermDate: e.target.value }))}
            />
            <InputField
              label="II termin (opcjonalnie)"
              type="date"
              min={termBounds.min}
              max={termBounds.max}
              value={bulkForm.secondTermDate}
              errorMessage={termDateError(bulkForm.secondTermDate) || undefined}
              onChange={(e) => setBulkForm((f) => ({ ...f, secondTermDate: e.target.value }))}
            />
            <InputField
              label="Przypisany kominiarz (opcjonalnie)"
              value={bulkForm.assignedTo}
              onChange={(e) => setBulkForm((f) => ({ ...f, assignedTo: e.target.value }))}
            />
            {/* A12: zarządca dla zaznaczonych (z innym zarządcą — pytanie po zapisie). */}
            <PlanClientFields
              idPrefix="bulk-plan"
              bulk
              clients={clients}
              value={bulkClient}
              onChange={setBulkClient}
              disabled={bulkSaving}
            />
            <PlanPrefillSourceField
              idPrefix="bulk-plan"
              bulk
              value={bulkSource}
              onChange={setBulkSource}
              disabled={bulkSaving}
            />
            {/* Przycisk „Zaplanuj" jest wyłączony bez I terminu — bez tej linijki klikanie
                w wyszarzony przycisk nie dawało ŻADNEJ informacji zwrotnej (QA 2026-08-05, D8). */}
            {!bulkForm.firstTermDate && (
              <Box data-testid="bulk-plan-hint" sx={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
                Podaj I termin, żeby zaplanować przegląd.
              </Box>
            )}
          </Box>
        )}
      </ModalShell>

      {/* Dodaj budynek (reużyty modal). */}
      {/* E4: paczka podsumowań wg filtrów — bez zaznaczania wierszy. */}
      <ModalShell
        open={packOpen}
        title="Paczka podsumowań wg filtrów"
        onClose={() => { if (!zipBusy) setPackOpen(false); }}
        width={520}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPackOpen(false)} disabled={zipBusy}>Anuluj</Button>
            <Button variant="primary" icon="download" onClick={submitPack} disabled={zipBusy}>
              {zipBusy ? 'Pakowanie…' : 'Pobierz paczkę'}
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
          {packError && (
            <Box
              role="alert"
              sx={{
                display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
                padding: 'var(--space-3) var(--space-4)',
                background: 'var(--status-deny-bg)',
                border: 'var(--border-width) solid var(--status-deny-bar)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--status-deny)', fontSize: 'var(--text-label)',
              }}
            >
              <Icon name="triangle-alert" size={18} />
              <span>{packError}</span>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <label
              htmlFor="pack-client"
              style={{
                fontFamily: 'var(--font-sans)', fontSize: 'var(--text-label)',
                fontWeight: 'var(--weight-semibold)', color: 'var(--text-secondary)',
              }}
            >
              Zarządca
            </label>
            <select
              id="pack-client"
              value={packForm.clientId}
              disabled={zipBusy}
              onChange={(e) => setPackForm({ ...packForm, clientId: e.target.value })}
              style={{
                minHeight: 'var(--touch-min)', padding: '0 var(--space-4)', boxSizing: 'border-box',
                fontFamily: 'var(--font-sans)', fontSize: 'var(--text-body)',
                color: 'var(--text-primary)', background: 'var(--surface-card)',
                border: 'var(--border-width-strong) solid var(--border-strong)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              <option value="">— wszyscy —</option>
              {clients.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
          </Box>

          <Box sx={{ display: 'flex', gap: 'var(--space-3)' }}>
            <InputField
              label="Termin od"
              type="date"
              value={packForm.dateFrom}
              disabled={zipBusy}
              onChange={(e) => setPackForm({ ...packForm, dateFrom: e.target.value })}
            />
            <InputField
              label="Termin do"
              type="date"
              value={packForm.dateTo}
              disabled={zipBusy}
              onChange={(e) => setPackForm({ ...packForm, dateTo: e.target.value })}
            />
          </Box>

          {/* E6: co ma wejść do paczki. Pkt 16 (2026-09-13): zamiast kart-checklist PROTOKOŁY lokali —
              dokładnie ten PDF, który daje ikona schowka pod budynkiem. */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <span
              style={{
                fontFamily: 'var(--font-sans)', fontSize: 'var(--text-label)',
                fontWeight: 'var(--weight-semibold)', color: 'var(--text-secondary)',
              }}
            >
              Zawartość paczki
            </span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={packForm.includeFindings}
                disabled={zipBusy}
                onChange={(e) => setPackForm({ ...packForm, includeFindings: e.target.checked })}
              />
              <span>Podsumowania kontroli</span>
            </label>
            <SummaryHeatSourcesOption
              disabled={zipBusy || !packForm.includeFindings}
              style={{ display: 'flex', paddingLeft: 'var(--space-6)' }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={packForm.includeProtocols}
                disabled={zipBusy}
                onChange={(e) => setPackForm({ ...packForm, includeProtocols: e.target.checked })}
              />
              <span>Protokoły lokali</span>
            </label>
          </Box>

          {/* E6: „z wyborem jednolokalowych / wielolokalowych". */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <label
              htmlFor="pack-building-type"
              style={{
                fontFamily: 'var(--font-sans)', fontSize: 'var(--text-label)',
                fontWeight: 'var(--weight-semibold)', color: 'var(--text-secondary)',
              }}
            >
              Typ budynku
            </label>
            <select
              id="pack-building-type"
              value={packForm.buildingType}
              disabled={zipBusy}
              onChange={(e) => setPackForm({ ...packForm, buildingType: e.target.value })}
              style={{
                minHeight: 'var(--touch-min)', padding: '0 var(--space-4)', boxSizing: 'border-box',
                fontFamily: 'var(--font-sans)', fontSize: 'var(--text-body)',
                color: 'var(--text-primary)', background: 'var(--surface-card)',
                border: 'var(--border-width-strong) solid var(--border-strong)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              <option value="">— wszystkie —</option>
              <option value="jednolokalowy">jednolokalowe</option>
              <option value="wielolokalowy">wielolokalowe</option>
            </select>
          </Box>

          <InputField
            label="Rok protokołu CEEB"
            type="number"
            min={2000}
            max={2100}
            placeholder="np. 2026"
            value={packForm.protocolYear}
            disabled={zipBusy}
            onChange={(e) => setPackForm({ ...packForm, protocolYear: e.target.value })}
            hint="Wybiera budynki, które mają protokół z tego roku. Treść podsumowań jest zawsze bieżąca."
          />

          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>
            Zakres dat dotyczy TERMINÓW przeglądu. Maksimum {200} budynków w jednej paczce —
            przy większym wyniku zawęź filtry.
          </p>
        </Box>
      </ModalShell>

      <AddBuildingModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={handleCreated}
        onShowExisting={handleShowExisting}
      />

      <InaccessiblePushConfirm
        building={inaccessibleConfirmFor}
        onConfirm={(b) => { void handlePushInaccessible(b); }}
        onCancel={() => setInaccessibleConfirmFor(null)}
      />

      {/* E2 Z4: obejście 409 TABLET_PENDING — tylko superadmin, świadomie wysyła wersję lokali z serwera. */}
      <ConfirmModal
        open={tabletOverrideFor !== null}
        title="Wysłać wersję z serwera?"
        message={(
          <>
            <p style={{ margin: 0 }}>{tabletOverrideFor ? ceebPush[tabletOverrideFor.id]?.error : ''}</p>
            <p style={{ margin: '8px 0 0' }}>
              Protokół <strong>{tabletOverrideFor?.address}</strong> zostanie wystawiony z danymi lokali, które są teraz na
              serwerze. Nowsza wersja z tabletu do niego nie trafi — po jej wysłaniu poprawka wymaga trybu poprawiania
              wystawionego protokołu. Obejście zostaje zapisane w historii.
            </p>
          </>
        )}
        confirmLabel="Wyślij mimo to"
        onConfirm={() => {
          const b = tabletOverrideFor;
          setTabletOverrideFor(null);
          if (!b) return;
          if (ceebPush[b.id]?.retry === 'finalize') void handleFinalizeInCeeb(b, { ignoreTabletPending: true });
          else void handlePushToCeeb(b, { skipFlueWarning: true, ignoreTabletPending: true });
        }}
        onCancel={() => setTabletOverrideFor(null)}
      />

      {/* pkt 14: „Zakres kontroli" pusty w CEEB — ostrzeżenie przed wysyłką, wysłać można. */}
      <ConfirmModal
        open={flueConfirmFor !== null}
        title="Dane zakresu kontroli nie zostały uzupełnione"
        message={(
          <p style={{ margin: 0 }}>
            W CEEB dla budynku <strong>{flueConfirmFor?.address}</strong> liczby przewodów (dymowe, spalinowe,
            wentylacyjne, awaryjne) są puste albo zerowe. Kominiarz uzupełnia je ręcznie w CEEB — wysyłka i tak
            może ruszyć. Wysłać mimo to?
          </p>
        )}
        confirmLabel="Wyślij mimo to"
        onConfirm={() => { const b = flueConfirmFor; setFlueConfirmFor(null); if (b) void handlePushToCeeb(b, { skipFlueWarning: true }); }}
        onCancel={() => setFlueConfirmFor(null)}
      />

      {/* feat/bulk-reschedule-unplan: zbiorcze odplanowanie — potwierdzenie z listą + wynik. */}
      <ConfirmModal
        open={bulkUnplanOpen && !bulkUnplanResult}
        title={`Odplanować ${budynkiLabel(unplannable().length)}?`}
        message={(
          <div>
            <p style={{ margin: '0 0 var(--space-3)' }}>
              Budynki wrócą do etapu <strong>Nowy</strong>, terminy zostaną usunięte. Przeglądy z zapisanymi wizytami
              albo z protokołem w CEEB backend zablokuje — pokażę je w wyniku.
            </p>
            <ul style={{ margin: 0, paddingLeft: 'var(--space-5)' }}>
              {unplannable().map((r) => <li key={r.id}>{r.address}</li>)}
            </ul>
          </div>
        )}
        confirmLabel={bulkUnplanBusy ? 'Odplanowuję…' : 'Odplanuj'}
        destructive
        onConfirm={() => { void runBulkUnplan(); }}
        onCancel={() => { if (!bulkUnplanBusy) setBulkUnplanOpen(false); }}
      />
      <ModalShell
        open={bulkUnplanOpen && !!bulkUnplanResult}
        title="Wynik odplanowania"
        onClose={() => { setBulkUnplanOpen(false); setBulkUnplanResult(null); }}
        footer={<Button variant="primary" onClick={() => { setBulkUnplanOpen(false); setBulkUnplanResult(null); }}>Zamknij</Button>}
      >
        {bulkUnplanResult && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', fontSize: 'var(--text-body)' }} data-testid="bulk-unplan-result">
            <span style={{ color: 'var(--status-ok)', fontWeight: 'var(--weight-semibold)' }}>✓ Odplanowano: {bulkUnplanResult.unplanned.length}</span>
            {bulkUnplanResult.blocked.length > 0 && (
              <div style={{ color: 'var(--status-rec)' }}>
                ⚠ Zablokowane: {bulkUnplanResult.blocked.length}
                <ul style={{ margin: 'var(--space-1) 0 0', paddingLeft: 'var(--space-5)', color: 'var(--text-primary)', fontSize: 'var(--text-caption)' }}>
                  {bulkUnplanResult.blocked.map((b, i) => <li key={i}>{b.address} — {b.reason}</li>)}
                </ul>
              </div>
            )}
            {bulkUnplanResult.errors.length > 0 && (
              <div style={{ color: 'var(--status-deny)' }}>
                ✕ Błędy: {bulkUnplanResult.errors.length}
                <ul style={{ margin: 'var(--space-1) 0 0', paddingLeft: 'var(--space-5)', fontSize: 'var(--text-caption)' }}>
                  {bulkUnplanResult.errors.map((b, i) => <li key={i}>{b.address}: {b.message}</li>)}
                </ul>
              </div>
            )}
          </Box>
        )}
      </ModalShell>

      {/* Migr. 073: „Do kolejki” / „Usuń z kolejki” — wiersz i zbiorczo; odmowy zostają w oknie. */}
      <QueueModal
        mode={queueTargets?.mode ?? 'add'}
        targets={queueTargets ? queueTargets.targets : null}
        onClose={() => setQueueTargets(null)}
        onDone={afterQueueChange}
      />

      {/* [Odplanuj] — potwierdzenie (pkt 24). Błąd 409 zostaje w modalu. */}
      <ConfirmModal
        open={unplanFor !== null}
        title="Odplanować przegląd?"
        message={(
          <div>
            <p style={{ margin: '0 0 var(--space-3)' }}>
              Budynek <strong>{unplanFor?.address}</strong> wróci do etapu <strong>Nowy</strong>, a zaplanowane
              terminy zostaną usunięte. Nie da się odplanować przeglądu, w którym są już zapisy lokali
              albo protokół w CEEB.
            </p>
            {unplanError && (
              <p role="alert" style={{ margin: 0, color: 'var(--status-error-text)', fontWeight: 'var(--weight-semibold)' }}>
                {unplanError}
              </p>
            )}
          </div>
        )}
        confirmLabel={unplanBusy ? 'Odplanowuję…' : 'Odplanuj'}
        destructive
        onConfirm={() => { void confirmUnplan(); }}
        onCancel={closeUnplan}
      />

      <ModalShell
        open={assignIds !== null}
        onClose={() => { if (!assignBusy) setAssignIds(null); }}
        width={520}
        title={`Przypisz zarządcę (${assignIds?.length ?? 0})`}
        footer={
          <>
            <Button variant="secondary" disabled={assignBusy} onClick={() => setAssignIds(null)}>Anuluj</Button>
            <Button variant="primary" disabled={!assignTarget || assignBusy} onClick={() => { void submitAssign(); }}>
              {assignBusy ? 'Przypisywanie…' : 'Przypisz'}
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', fontFamily: 'var(--font-sans)' }}>
          {/* B3 (Bronek 21.10): wpisz kilka liter nazwy albo skrótu zamiast przewijać ~70 zarządców. */}
          <ComboBox
            id="bulk-assign-client-select"
            label="Przypisz do zarządcy"
            placeholder="Wpisz nazwę lub skrót zarządcy"
            value={assignTarget}
            disabled={assignBusy}
            options={clientComboOptions(clients)}
            onChange={setAssignTarget}
          />
          <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
            Budynki bez zarządcy zostaną przypisane od razu. Jeśli któryś ma już innego zarządcę, zapytamy o zmianę.
          </span>
          {assignError && (
            <Box role="alert" sx={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--status-deny)', fontSize: 'var(--text-label)' }}>
              <Icon name="triangle-alert" size={18} />
              <span>{assignError}</span>
            </Box>
          )}
        </Box>
      </ModalShell>
      <ConfirmModal
        open={!!reassignAsk}
        title="Zmienić zarządcę?"
        message={reassignAsk ? (
          <div>
            <p style={{ margin: 0 }}>
              {reassignAsk.attached > 0 ? `Przypisano już ${budynkiLabel(reassignAsk.attached)} bez zarządcy. ` : ''}
              Te budynki mają innego zarządcę — zmienić na „{targetName(reassignAsk.targetId)}"?
            </p>
            <ul style={{ margin: 'var(--space-2) 0', paddingLeft: 'var(--space-5)' }}>
              {reassignAsk.others.map((o) => <li key={o.id}>{o.address} — teraz: {o.clientName || '—'}</li>)}
            </ul>
          </div>
        ) : ''}
        confirmLabel="Zmień zarządcę"
        cancelLabel="Zostaw obecnego"
        onConfirm={() => { void confirmReassign(); }}
        onCancel={skipReassign}
      />
      <PipelineBulkHelpModal open={bulkHelpOpen} onClose={() => setBulkHelpOpen(false)} />
      <ScheduleImportModal open={importOpen} onClose={() => setImportOpen(false)} onDone={refresh} />
      <UncheckedApartmentsModal
        buildingIds={uncheckedIds}
        subject={uncheckedIds && uncheckedIds.length === 1 ? addressOf(uncheckedIds[0]) : undefined}
        onClose={() => setUncheckedIds(null)}
      />
      <HeatSourceSummaryModal
        selection={hsSummaryIds ? { buildingIds: hsSummaryIds } : null}
        onClose={() => setHsSummaryIds(null)}
      />
      <ScheduleMailModal
        buildingIds={scheduleMail?.ids ?? null}
        defaultTo={scheduleMail?.defaultTo}
        defaultToLabel={scheduleMail?.label}
        onClose={() => setScheduleMail(null)}
        onSent={(r) => {
          setScheduleMail(null);
          setZipMsg({
            kind: 'info',
            text: `Wysłano grafik (${budynkiLabel(r.buildings)}) na: ${r.to.join(', ')}.${r.skippedCount > 0 ? ` Pominięto (bez terminu): ${budynkiLabel(r.skippedCount)}.` : ''}`,
          });
        }}
      />
      <CaseNumbersModal buildingIds={caseNumbersIds} onClose={() => setCaseNumbersIds(null)} />
      <CeebEditIssuedModal
        target={editIssuedFor ? { buildingId: editIssuedFor.id, address: editIssuedFor.address } : null}
        onCancel={() => setEditIssuedFor(null)}
        onStarted={(jobId, dryRun) => { if (editIssuedFor) handleEditIssuedStarted(editIssuedFor, jobId, dryRun); }}
      />
    </div>
  );
};

export default PipelineView;
